import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Alert, Button, Form, Modal, Spin, Tooltip } from 'antd';
import { CalendarClock, HelpCircle, PanelRight, TriangleAlert } from 'lucide-react';
import type { DateTime } from 'luxon';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import type {
  ArrangementDto,
  OrgUnitSummaryDto,
  Paginated,
  PolicyListItemDto,
  PolicyVersionDto,
  RankDto,
  RequirementLineInputDto,
  AttachmentDto,
  SharedColumnInputDto,
} from '@pams/shared';
import {
  PAGINATION,
  arrangementDisplayTitle,
  deriveArrangementTitle,
  generateSlots,
  istDutyDate,
  lineKeyOf,
  type Slot,
} from '@pams/shared';
import { IntakeForm } from '@/components/intake/IntakeForm';
import { ArchiveNote, ConfirmDestructive } from '@/components/ConfirmDestructive';
import { PageHeader } from '@/components/PageHeader';
import { useAuth } from '@/hooks/useAuth';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import type { CoveredColumn } from '@/components/intake/ForceGrid';
import { commandAsksByDutyPoint, duplicateCommandEntries } from '@/components/intake/commandAsks';
import {
  anchoredKeys,
  coveringKeys,
  disableSharing,
  enableSharing,
  normalizeSharing,
  removeCard,
  seatsLostOnConverge,
  asksLostOnConverge,
  submittableSharedColumns,
  type SharingState,
  type ZeroLineFor,
} from '@/lib/sharedColumns';
import {
  armingOf,
  guardReasonOf,
  hasMeaningfulContent,
  instructionIsReal,
  saveIsPending,
  supportingPersonIsReal,
  triggerMaySave,
  type SaveState,
  type SaveTrigger,
} from './autosave';
import { autoSaveEnabled } from '@/lib/runtimeConfig';
import type { PendingAttachment } from '@/components/attachments/AttachmentList';
import { ARRANGEMENTS_NAV, labelOf } from '@/lib/navigation';
import {
  cameViaRecord,
  homeListOf,
  listAfterLeaving,
  listOriginOf,
  originForOrder,
  withListOrigin,
} from '@/lib/listOrigin';
import { fetchScopedOrgTree } from '@/lib/orgTree';
import { defaultPolicyFor } from '@/lib/policyDefault';
import { UNAVAILABLE_LOOK, publishControlState } from './publishControl';
import { can, READ_ONLY_TOOLTIP } from '@/lib/accessRole';
import { api, type ApiError } from '@/lib/api';
import { disclosure, willCreateSummary } from '@/lib/arrangementShape';
import { toGrid } from '@/lib/forceGrid';
import { awaitedIsLive } from '@/lib/commandWords';
import { resolvePolicyColumns } from '@/lib/policyColumns';
import { queryKeys } from '@/lib/queryKeys';
import { notifyDevChange } from '@/lib/devNotice';
import { cn } from '@/lib/utils';
import { checklistOf, stepOfSection } from '@/lib/sectionRegistry';
import {
  AUTOSAVE_COPY,
  DISCARD_COPY,
  DRAFT_EXPIRY_COPY,
  FORM_STEPS,
  GUARD_TITLE,
  HELP_PANEL,
  PUBLISH_COPY,
  SAVE_COPY,
  SHARE_COPY,
  STEP_COPY,
} from './copy';
import type { PublishPreviewDto } from '@pams/shared';
import { draftExpiryNotice, draftHasExpired } from './draftExpiry';
import { RightRail } from './components/RightRail';
import { ARRANGEMENT_SECTIONS } from './sections';
import type { ArrangementSectionActions, ArrangementSectionState, DutyPointView } from './sections';
import {
  DEFAULT_SHIFT,
  emptyValues,
  fromArrangement,
  laterOfTodayAnd,
  toDutyPointInput,
  type DutyPointFormValue,
  scheduleOfDraft,
  mintDutyPointId,
  mintShiftId,
  toDraft,
  type ArrangementFormValues,
} from './formValues';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE POLICE ARRANGEMENT INTAKE. ONE PAGE, NOT A WIZARD.
 *
 *  A wizard hides fields behind Next buttons and makes people hunt for what they already
 *  filled. One anchored page shows the whole job, and the right column's checklist is the
 *  navigation — which is why it carries completion ticks rather than a progress bar.
 *
 *  ⚠️ THE FORM GROWS AND SHRINKS. THERE IS NO TYPE PICKER ANYWHERE ON IT.
 *  The client describes four kinds of arrangement; three of them are this one form at
 *  three depths, and the depth follows from what has been entered. Every conditional on
 *  this page reads `disclosure()` from `lib/arrangementShape.ts`, which is asserted
 *  against exit criteria 2, 3 and 4. A predicate written inline here is how that leaks.
 *
 *  ─── ⚠️ THE `Form.useWatch` SELECTOR, AND THE TRAP IT AVOIDS ────────────────────
 *
 *  The whole right column derives from one watched draft. `useZodForm` calls
 *  `Form.useForm()` with no generic, so the PLAIN `useWatch` overload yields `any`; the
 *  SELECTOR overload keeps it typed. And rc-field-form compares `JSON.stringify` of the
 *  selected value, falling back to `Math.random()` when that throws — so `toDraft` returns
 *  strings, numbers, booleans and arrays only, never the luxon `DateTime` the pickers
 *  hold. `PolicyBuilder`'s header records both; this is the second consumer to meet them.
 *
 *  ─── ⚠️ THE PUBLISH BAR IS A REAL FOOTER, NOT A STICKY ELEMENT ──────────────────
 *
 *  This page OWNS ITS OWN SCROLLING. The root is `h-full` inside `<main>`, the form is the
 *  one `flex-1 overflow-y-auto` child, and the bar is a `shrink-0` sibling beneath it. So
 *  `<main>` never scrolls, there is never a second scrollbar, and the bar sits on the
 *  bottom edge with nothing below it.
 *
 *  ⚠️ IT USED TO BE `sticky bottom-0` AND THAT WAS WRONG IN TWO VISIBLE WAYS. A sticky
 *  element is in normal flow, so `<main>`'s own `py-6` stayed BELOW it as dead space; and
 *  while the page was mid-scroll the bar floated over the content rather than bounding it.
 *  A footer that is the last flex child of a fixed-height column cannot do either.
 *
 *  The right column is still capped, because a sticky element taller than its scroll
 *  viewport is unreachable at its bottom and `TwoColumnLayout` deliberately does not know
 *  how tall its children are.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ `unsaved` IS A STATE, NOT THE ABSENCE OF ONE. "Nothing has happened yet" and "you have
 * changes that are not on the server" are different facts and the operator needs the second
 * one said out loud, in a colour that means it.
 */

/**
 * A fresh card, anchored on the order date. §1d — the schedule inherits it.
 *
 * ─── ⚠️ [C2b §8g] EXCEPT THAT IT NEVER ANCHORS IN THE PAST ─────────────────────────────
 *
 * §8g asks for the event dates to be independent of the order date and to default to today.
 * Both are already true of a NEW order — `emptyValues()` sets `paDate` to today and this line
 * inherits it — and neither was true of a card added to an order raised LAST MONTH: the new
 * duty point silently arrived dated in the past, which publishes as a duty whose window has
 * already closed and which `deleteImpact` then reads as FILING.
 *
 * ⚠️ THE ORDER DATE STILL WINS WHEN IT IS IN THE FUTURE, and that is the half that must not
 * change: an order dated 20 August is being written for 20 August, and re-anchoring it on today
 * would make the operator type the date into every card. This only takes the LATER of the two.
 *
 * ⚠️ AND IT IS A SEED, NOT A FLOOR. The field is freely editable afterwards — dating a duty in
 * the past is legal and is how a missed order is recorded — so this changes what is OFFERED and
 * refuses nothing. [D4]'s split between the ADMINISTRATIVE `pa_date` and the OPERATIONAL duty
 * dates is what makes the two genuinely independent, and this is that split at the seed.
 */
const newDutyPoint = (paDate: DateTime | undefined, policyId: string | undefined): DutyPointFormValue => ({
  id: mintDutyPointId(),
  places: [{ placeId: null, name: '' }],
  policyId,
  timingKind: 'NO_FIXED_TIME',
  shifts: [DEFAULT_SHIFT()],
  recurrenceKind: 'ONE_DAY',
  startDate: laterOfTodayAnd(paDate),
  endsKind: 'ON_DATE',
  weekdays: [],
  commandEntries: [],
  requirementLines: [],
  instructions: [],
  supportingArrangements: [],
});

/** One id, shared by the toggle's `aria-controls` and the slot it opens. */
const SUMMARY_PANEL_ID = 'arrangement-summary-panel';

/**
 * One requirement line per usable column, in the version's own order, all at zero.
 *
 * ⚠️ `postRows`/`vehicleRows`, NEVER the raw arrays: a card being seeded is never offered an
 * ARCHIVED column, while `allPostTypes` still RESOLVES one an existing card already carries.
 * Two different questions — `lib/policyColumns.ts` states the split.
 *
 * ⚠️ [C5c §2] `exclude` IS THE CARD'S COVERED KEYS. A column another card asks for on this one's
 * behalf is not seeded here — seeding it would put the column back on a covered card, which is the
 * "counted twice" the sharing exists to remove (and which the server refuses as LINE_ON_COVERED).
 */
const seededLines = (
  columns: ReturnType<typeof resolvePolicyColumns>,
  exclude: ReadonlySet<string> = new Set(),
): RequirementLineInputDto[] =>
  [
    ...columns.postRows.map((row) => ({
      postTypeId: row.id,
      vehicleTypeId: null,
      adHocLabel: null,
      requiredCount: 0,
      allocations: [],
    })),
    ...columns.vehicleRows.map((row) => ({
      postTypeId: null,
      vehicleTypeId: row.id,
      adHocLabel: null,
      requiredCount: 0,
      allocations: [],
    })),
  ].filter((line) => !exclude.has(lineKeyOf(line)));

/**
 * ⚠️ [C1a §2] "NOBODY HAS TOUCHED THIS GRID" — AND EACH CLAUSE IS LOAD-BEARING.
 *
 * It decides whether a card whose policy moved is re-seeded SILENTLY or offered the change,
 * so a false positive here destroys somebody's typing.
 *
 *  • `adHocLabel == null` — a one-off column is typed work by definition (FR-5.2.6c), and a
 *    re-seed would delete it outright.
 *  • `requiredCount === 0` — `seededLines` writes exactly zero, so anything else is a number
 *    a person entered.
 *  • `allocations.length === 0` — the sharp one. `fromGrid` writes an allocation entry per
 *    source row EVEN AT ZERO, precisely so a row being typed into survives the round trip. So
 *    "no allocations anywhere" is the only available proof that no source has been picked;
 *    testing the counts alone would silently delete a division chosen a second ago.
 *
 * An empty array satisfies `.every` trivially, which is the untouched case by definition.
 */
const untouchedGrid = (lines: readonly RequirementLineInputDto[]): boolean =>
  lines.every((line) => line.adHocLabel == null && line.requiredCount === 0 && line.allocations.length === 0);

/** The non-ad-hoc column ids a card's lines name. Ad-hoc lines belong to the ORDER, not the policy. */
const carriedColumnIds = (lines: readonly RequirementLineInputDto[]): string[] =>
  lines.flatMap((line) => (line.adHocLabel ? [] : [line.postTypeId ?? line.vehicleTypeId ?? ''])).filter(Boolean);

/** Later wins, so a rename in the newer version lands while the older one keeps naming what it dropped. */
const mergedLabels = <T,>(older: ReadonlyMap<string, T>, newer: ReadonlyMap<string, T>): ReadonlyMap<string, T> =>
  new Map([...older, ...newer]);

const ArrangementBuilderPage = () => {
  const { arrangementId } = useParams<{ arrangementId?: string }>();
  const { officer } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();

  const [form] = Form.useForm<ArrangementFormValues>();
  /*
   * ─────────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [AUTO-SAVE] DOES THIS PAGE SAVE ON ITS OWN? — `ENABLE_AUTO_SAVE`, read ONCE PER MOUNT.
   *
   *  The container writes it into `/runtime-config.js` (`lib/autoSaveSetting.ts` has the rules),
   *  so it is a deployment fact rather than a build one. Off, blur, step change, unmount and
   *  attach write nothing (`triggerMaySave`), the Save button is the one way work is kept, and the
   *  leave guard stops every exit that would lose an unsaved change — see the guard below.
   *
   *  ⚠️ STATE WITH A LAZY INITIALISER, NEVER A MODULE CONSTANT: a value read at import would be
   *  frozen for every later mount, and a test sets `window.__PAMS_CONFIG__` per test. And never
   *  re-read mid-sitting — a page that changed how it saves under somebody typing would be worse
   *  than either mode.
   * ─────────────────────────────────────────────────────────────────────────────────────
   */
  const [autoSave] = useState(autoSaveEnabled);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  /*
   * ⚠️ [C3a §7d] A REF MIRROR, WRITTEN BY ONE FUNCTION — `dirtyRef`'s shape and its reason.
   * `markDirty` needs to know whether it has ALREADY claimed a save is owed, and a burst of
   * `onValuesChange` calls inside one tick would all read the same stale render value. Every
   * write goes through `writeSaveState`, so the two cannot separate.
   */
  const saveStateRef = useRef<SaveState>('idle');
  const writeSaveState = (next: SaveState): void => {
    saveStateRef.current = next;
    setSaveState(next);
  };
  /**
   * ─────────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3a §3] PDFs CHOSEN BEFORE THE RECORD EXISTS — HELD, NEVER UPLOADED AGAINST NOTHING.
   *
   *  The draft is created LAZILY, on the first armed autosave, so Create page 1 has no id to
   *  attach to. The attach control therefore QUEUES the file, marks the form dirty and fires
   *  `flush('attach')`; the one-writer save creates the record, and the effect below drains.
   *
   *  ⚠️ STATE, NOT A REF — the list renders, so a queued file has to cause a render or the
   *  operator sees nothing happen.
   * ─────────────────────────────────────────────────────────────────────────────────────
   */
  const [pendingAttachments, setPendingAttachments] = useState<readonly PendingAttachment[]>([]);
  /*
   * ─────────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3a §3] A REF BESIDE THE STATE, FOR `dirtyRef`'s REASON EXACTLY — AND WITHOUT IT §3'S
   *  WHOLE PROMISE IS FALSE.
   *
   *  `queueAttachment` sets the queue and calls `flush('attach')` in the SAME TICK, so a `flush`
   *  reading the STATE reads the value from the render that has not happened yet — `length === 0`
   *  for the first file. `hasMeaningfulContent`'s third disjunct is then false, `armingOf` answers
   *  `'no-content'`, and the save returns SILENTLY: attaching a PDF to an untouched form creates
   *  no draft at all, which is the one thing §3 exists to make impossible.
   *
   *  It is the same defect `dirtyRef` already exists to prevent, one field over — `flush` reads
   *  `dirtyRef.current` rather than `dirty` for exactly this reason, on the line above.
   *
   *  ⚠️ ONE WRITER, `writePending`, so the two cannot drift. Never `setPendingAttachments`
   *  directly — `markClean`'s rule, and the same failure if it is broken.
   * ─────────────────────────────────────────────────────────────────────────────────────
   */
  const pendingRef = useRef<readonly PendingAttachment[]>([]);
  /* ⚠️ The key currently being uploaded — see the drain effect. `null` when nothing is. */
  const drainingRef = useRef<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  /**
   * ⚠️ [D4.1] CLOSED ON EVERY LOAD, AT EVERY WIDTH, AND DELIBERATELY NOT REMEMBERED.
   *
   * Plain state, not `useColumnVisibility`'s URL-and-storage pair. A remembered sidebar is a
   * remembered DEFAULT, and the whole point of the change is that the form gets the measure
   * unless the operator asks otherwise. It survives a step change and a save because it is
   * page state; it does not survive a reload, which is exactly the line the brief drew.
   */
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [blockersOpen, setBlockersOpen] = useState(false);
  const [step, setStep] = useState(0);
  /**
   * ⚠️ WHICH CARDS ARE OPEN, HELD BY THE PAGE RATHER THAN BY EACH CARD.
   *
   * `Add duty point` and `Duplicate` open the card they create and collapse the rest, which
   * is what makes expanding cost ZERO clicks on the budget path — a card cannot do that to
   * its siblings from its own state.
   */
  const [openCards, setOpenCards] = useState<string[]>([]);
  const [savedId, setSavedId] = useState<string | undefined>(arrangementId);
  /** ⚠️ A form opened on an existing record is NOT ready to autosave until it is seeded. */
  /**
   * ⚠️ A REF, NOT STATE, AND THE DIFFERENCE IS NOT STYLE. Nothing renders `hydrated`; it
   * gates ONE event handler. As state it forced a re-render of the whole form for a value
   * no pixel depends on, and — because it has to flip AFTER `setFieldsValue` rather than
   * with the data that caused it — it could not be derived either.
   */
  const hydrated = useRef(!arrangementId);
  /**
   * ⚠️ [C1f §6] `dirty` AS A REF AS WELL AS STATE, AND THIS IS NOT STYLE.
   *
   * `TimeField` commits ON BLUR: its `onChange` → `Form.Item` → `onValuesChange` →
   * `setDirty(true)` all run synchronously inside the same `focusout`, and the form's own
   * blur handler — an ANCESTOR in the bubble phase — would read the PREVIOUS render's `dirty`
   * and skip the save. That is `hydrated`'s own argument, applied to a second value.
   */
  const dirtyRef = useRef(false);
  /** ⚠️ [C1f §6] One write in flight; a second trigger queues rather than racing. */
  const inFlight = useRef(false);
  const queued = useRef(false);
  /** ⚠️ [C1f §6] One toast per DISTINCT message — see the save mutation's `onError`. */
  const lastError = useRef<string | null>(null);
  /**
   * ⚠️ [C1f §6] `flush` REACHED THROUGH A REF, so the unmount cleanup and the mutation's own
   * `onSettled` can call the CURRENT one. A cleanup closes over the render it was created in,
   * and the last render before an unmount is exactly the one holding the unsaved work.
   */
  const flushRef = useRef<(trigger: SaveTrigger) => void>(() => undefined);
  /** Which record's server copy has already been written into the form. */
  const seededFor = useRef<string | undefined>(undefined);
  const sectionRefs = useRef(new Map<string, HTMLElement>());

  const canWrite = officer ? can(officer.accessRole, 'arrangement.write') : false;
  const canPublish = officer ? can(officer.accessRole, 'arrangement.publish') : false;
  /**
   * ⚠️ [D4.5 §2a] `arrangement.delete` — WHOEVER MAY ARCHIVE A DRAFT TODAY, AND NO NEW GRANT.
   *
   * It is read rather than assumed equal to `arrangement.write`: [LIFECYCLE] widened this
   * one to `NODAL_ADMIN` on the argument that an arrangement HAS a scope column and `Scope`
   * confines it, which is a different argument from the write grant's. `pages/ArrangementDetail`
   * gates its own archive on exactly this.
   */
  const canDelete = officer ? can(officer.accessRole, 'arrangement.delete') : false;
  /**
   * ⚠️ [amended C3c §9c] NO LONGER REACHABLE THROUGH THE ROUTE TABLE, AND IT STAYS.
   *
   * `/arrangements/:id/edit` now carries `arrangement.write` + `RequireDivision`, exactly as
   * `/arrangements/new` always did, so a read-only officer no longer arrives here at all.
   * §9's no-dead-tooling rule would ordinarily delete a branch nobody can reach; this one is
   * kept, and three things would each have to stop being true before it goes:
   *
   *   · `autosave.test.ts` exercises `armingOf`'s `'read-only'` arm as a UNIT, and every
   *     builder suite mounts this component DIRECTLY rather than through the route table — so
   *     the gate is not this component's coverage and the branch is not untested;
   *   · `armingOf` is EXHAUSTIVE, and deleting an arm removes one honest answer to "why did
   *     that not save?";
   *   · a route guard is a client courtesy and not a control (`RequireAuth.tsx`'s own header
   *     says so). The API is the gate; a component correct in isolation is what makes that safe.
   */
  const readOnly = !canWrite;

  // ── The masters this form's shape depends on. ─────────────────────────────────
  /**
   * ⚠️ [D4.5 §1b] `show: 'all'`, AND THAT DOES NOT WIDEN WHAT MAY BE CHOSEN.
   *
   * It used to send no `show`, so the server defaulted to `current` and an ARCHIVED policy
   * was absent from the payload entirely. A saved order pointing at one then had a
   * `policyId` its `Select` had no option for, and rc-select falls through to
   * `label ?? item.value` — so the card rendered the policy's ObjectId
   * (`lib/selectOptions.ts` has the mechanism, read out of the library).
   *
   * The CHOOSABLE set is still `activePolicies` below, unchanged. This fetch only makes the
   * archived row RESOLVABLE, which is `lib/policyColumns.ts`'s rule: filter at USE, never
   * lose the record. `MAX_LIMIT` rather than 50 for the same reason — a 51st policy was a
   * third way to produce the same defect, and the bound stays a bound.
   */
  const policies = useQuery({
    queryKey: queryKeys.policies.list({ page: 1, limit: PAGINATION.MAX_LIMIT, show: 'all' }),
    queryFn: ({ signal }) =>
      api.get<Paginated<PolicyListItemDto>>('/policies', {
        query: { page: 1, limit: PAGINATION.MAX_LIMIT, show: 'all' },
        signal,
      }),
    /*
     * ⚠️ [amended PRE-P6 §3a] ALWAYS RE-READ ON MOUNT, AND THIS IS THE SECOND HALF OF §3a.
     *
     * The client no longer pins a version onto the wire, so a stale list can no longer bind
     * the wrong one — but it can still SHOW the wrong one. `queryClient.ts` sets
     * `staleTime: 30_000` with `refetchOnWindowFocus: false`, which is right for a list page
     * and wrong for the one screen whose whole job is choosing between policies: an operator
     * who publishes v2 in another tab and comes back here would read "v1" beside the picker
     * and reasonably conclude the publish had not worked.
     *
     * One extra request per visit to a screen that already fires several, against a bounded
     * list of about a dozen rows.
     */
    refetchOnMount: 'always',
  });

  /**
   * ⚠️ ENABLED AND PUBLISHED ONLY — §5b. `archivedAt === null` is [LIFECYCLE]'s axis and
   * `currentVersionId` is P2's denormalised pointer: a policy with no published version has
   * no columns to offer, so it cannot raise anything.
   */
  const activePolicies = useMemo(
    () => (policies.data?.items ?? []).filter((policy) => policy.archivedAt === null && policy.currentVersionId),
    [policies.data],
  );

  /**
   * What the card's searchable dropdown shows: name, a description line, and — [C2b §8e] —
   * WHAT THE POLICY ASKS FOR.
   *
   * ⚠️ THE COLUMNS ARE THE HONEST PREVIEW AND THE DESCRIPTION IS NOT. `policyListItemSchema`
   * already says so at `postTypes`: "an operator scanning this list wants to know what a policy
   * ASKS FOR, and a sentence somebody typed once is not that." Choosing a policy on this screen
   * decides the force grid's whole shape, and until now the only way to find out which shape was
   * to choose it and look.
   *
   * ⚠️ THE LIST ENDPOINT ALREADY RETURNS THEM, so this adds no request — `policies.data` is the
   * same query the resolver below reads.
   */
  const policyOptions = useMemo(
    () =>
      activePolicies.map((policy) => ({
        id: policy.id,
        name: policy.name,
        description: policy.description ?? null,
        isDefault: policy.isDefault,
        /*
         * ⚠️ `?? []` ON BOTH, AND IT IS NOT DEFENSIVE PADDING. This is a PREVIEW — a native
         * `title` on a dropdown option — and a decorative read must never be able to take down
         * the screen that raises orders. Both keys are required on the wire, so the reachable
         * case is a caller that builds the list some other way; degrading to "no preview" is
         * the only acceptable failure, and it caught nineteen tests doing exactly that.
         */
        columns: [
          ...(policy.postTypes ?? []).map((row) => row.shortName || row.name),
          /* A vehicle type has a `code` rather than a short name — one vocabulary each. */
          ...(policy.vehicleTypes ?? []).map((row) => row.code || row.name),
        ],
      })),
    [activePolicies],
  );

  /**
   * ⚠️ [D4.5 §1b] WHAT AN ALREADY-STORED `policyId` RENDERS AS — every policy, archived ones
   * included. The counterpart to `policyOptions`, which is what a card may CHOOSE.
   */
  const resolvePolicy = useMemo(() => {
    const byId = new Map((policies.data?.items ?? []).map((policy) => [policy.id, policy]));
    return (id: string): { name: string } | undefined => byId.get(id);
  }, [policies.data]);

  const watched = Form.useWatch((values: unknown) => toDraft(values), form);
  const draft = watched ?? toDraft(emptyValues());

  /**
   * ⚠️ [amended D4] ONE VERSION QUERY PER DISTINCT POLICY ON THE ORDER, NOT ONE FOR IT.
   *
   * A card names its own policy, so a three-card order can span three. `useQueries` keeps
   * that a fixed number of hooks whatever the stack does — a query per CARD would be a hook
   * in a loop, which React forbids and which would break the moment a card is removed.
   *
   * ⚠️ STILL `Policy.currentVersionId`, THE DENORMALISED POINTER — never the draft. It is
   * written by exactly one writer, the publish transaction, which is why reading it is safe
   * and why a duty point snapshots a VERSION rather than a policy.
   */
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [amended PRE-P6 §3a] WHICH VERSION A CARD ACTUALLY USES — ITS OWN, OR THE CURRENT.
   *
   *  This used to fetch `policy.currentVersionId` per POLICY and hand it to every card on
   *  that policy. That was only ever right because the effect below then FORCED each card
   *  onto it (see the deleted branch there) — which is the very thing §3a removes, because
   *  it silently re-pointed a saved draft's duty point at whatever version happened to be
   *  current when somebody reopened it.
   *
   *  With that gone, a card keeps the version it was raised against, so the columns have to
   *  be resolved from THAT version. Fetching the current one and rendering a v1 card from it
   *  would print "—" for a column v2 dropped, and would trip the §5c fork test on an order
   *  nobody has edited.
   *
   *  ⚠️ THE FALLBACK READS THE WHOLE POLICY LIST, NOT `activePolicies`. §5b's comment above
   *  says the `show=all` fetch exists to make an ARCHIVED policy resolvable; keying the
   *  fallback on the choosable set would have kept it unresolvable, which is
   *  `lib/policyColumns.ts`'s rule broken one level up.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [amended C1a §2] TWO REFS PER CARD, BECAUSE A CARD ANSWERS TWO QUESTIONS.
   *
   *  BINDING — "which version will this be SAVED against". The card's own when it names
   *  one, the policy's current pointer when it does not (which is what omitting
   *  `policyVersionId` asks the server to resolve — [PRE-P6] §3a). It is what the `v{n}`
   *  label states, what seeding takes its columns from, and what `forks` is measured
   *  against.
   *
   *  COLUMNS-FROM — "which version are the requirement lines ON SCREEN from". Recorded
   *  when they were seeded. The two are the same for every card until somebody publishes a
   *  new version while a draft is open, and THAT divergence is the reported defect: the
   *  label followed the pointer and the grid did not.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const bindingRefFor = useCallback(
    (point: { policyId?: string; policyVersionId?: string }): { policyId: string; versionId: string } | null => {
      if (!point.policyId) return null;
      const known = policies.data?.items.find((row) => row.id === point.policyId);
      /*
       * ⚠️ `||`, NOT `??`. The watched draft carries `''` for "no version" — it is
       * JSON-serialisable by rule (`draft.ts`), so it cannot carry `undefined` — and `??`
       * would take the empty string as a real id and resolve nothing at all.
       */
      const versionId = point.policyVersionId || known?.currentVersionId || '';
      return versionId ? { policyId: point.policyId, versionId } : null;
    },
    [policies.data],
  );

  const columnsRefFor = useCallback(
    (point: {
      policyId?: string;
      policyVersionId?: string;
      columnsFromVersionId?: string;
    }): { policyId: string; versionId: string } | null => {
      if (!point.policyId) return null;
      // No memory means no claim: fall back to the binding, and nothing below fires.
      if (!point.columnsFromVersionId) return bindingRefFor(point);
      return { policyId: point.policyId, versionId: point.columnsFromVersionId };
    },
    [bindingRefFor],
  );

  const cardVersionRefs = useMemo(() => {
    const byVersion = new Map<string, { policyId: string; versionId: string }>();
    for (const point of draft.dutyPoints) {
      // BOTH, so a card mid-divergence can still NAME the column it is about to lose.
      for (const ref of [bindingRefFor(point), columnsRefFor(point)]) {
        if (ref) byVersion.set(ref.versionId, ref);
      }
    }
    return [...byVersion.values()];
  }, [draft.dutyPoints, bindingRefFor, columnsRefFor]);

  const versionQueries = useQueries({
    queries: cardVersionRefs.map(({ policyId, versionId }) => ({
      queryKey: queryKeys.policies.version(policyId, versionId),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.get<PolicyVersionDto>(`/policies/${policyId}/versions/${versionId}`, { signal }),
    })),
  });

  /** Keyed on the VERSION id, which is globally unique — two cards may sit on two versions. */
  const versionById = useMemo(() => {
    const byId = new Map<string, PolicyVersionDto>();
    cardVersionRefs.forEach((ref, index) => {
      const data = versionQueries[index]?.data;
      if (data) byId.set(ref.versionId, data);
    });
    return byId;
  }, [cardVersionRefs, versionQueries]);

  const versionOf = useCallback(
    (point: { policyId?: string; policyVersionId?: string }): PolicyVersionDto | undefined => {
      const ref = bindingRefFor(point);
      return ref ? versionById.get(ref.versionId) : undefined;
    },
    [bindingRefFor, versionById],
  );

  /** What the grid currently RENDERS from — see the two-refs header above. */
  const columnsVersionOf = useCallback(
    (point: {
      policyId?: string;
      policyVersionId?: string;
      columnsFromVersionId?: string;
    }): PolicyVersionDto | undefined => {
      const ref = columnsRefFor(point);
      return ref ? versionById.get(ref.versionId) : undefined;
    },
    [columnsRefFor, versionById],
  );

  /**
   * ⚠️ [C5c §2e] WHAT A CARD'S OWN VERSION OFFERS FOR ONE COLUMN, AT ZERO — `lib/sharedColumns.ts`'
   * question when it gives a column back to a card that set nothing aside (one added after sharing
   * began). The BINDING version, because that is what the card will be saved against.
   */
  const zeroLineFor = useCallback<ZeroLineFor>(
    (card, key) => {
      const version = versionOf(card);
      return version ? seededLines(resolvePolicyColumns(version)).find((line) => lineKeyOf(line) === key) : undefined;
    },
    [versionOf],
  );

  const existing = useQuery({
    queryKey: queryKeys.arrangements.detail(arrangementId ?? ''),
    queryFn: ({ signal }) => api.get<ArrangementDto>(`/arrangements/${arrangementId ?? ''}`, { signal }),
    enabled: Boolean(arrangementId),
  });

  /*
   * ─────────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [TYPED-BACK] THE LIST THIS ORDER WAS OPENED FROM — `?from=` — AND WHICH DOOR IT CAME IN BY.
   *
   *  `listOrigin.ts` holds the whole rule for what `?from=` may name (a registered typed list or
   *  nothing, so no open redirect). Two more readings, both here:
   *
   *  ⚠️ THE ORIGIN IS KEPT ONLY FOR AN ORDER OF ITS OWN TYPE (`originForOrder`). A typed list holds only
   *  its own type and a corrigendum inherits its parent's, so every path the UI builds agrees; what is
   *  refused is a hand-made link — an untyped order, or a NEW one (never typed, so `existing.data` has
   *  no type and every origin is refused on the create route). Until the record loads nothing is kept,
   *  and nothing renders that reads it: the spinner below stands in for the whole page.
   *
   *  ⚠️ …EXCEPT A RECORD THAT FAILED TO LOAD (`isLoadingError`, never `isError`): its type is unknowable
   *  and its record page would fail too, with no back link of its own, so the list is the only way
   *  back worth offering. `isLoadingError` and not `isError`, because a BACKGROUND refetch that fails
   *  keeps the data — the record did load, its type is known, and the back link must not flip under it.
   *
   *  ⚠️ `viaRecord` IS THE RECORD PAGE'S OWN DOOR SAYING SO (`withRecordOrigin`'s `?via=record`). The
   *  back link reads it below; the leaving acts do not — leaving goes to the list whichever door it was.
   *
   *  ⚠️ READ FROM THE ROUTER'S LOCATION, which [C1f §6]'s first-save `replaceState` leaves stale on the
   *  create route — the one place this page reads it, and harmless there: the create route keeps no
   *  origin at all, before its first save or after it.
   * ─────────────────────────────────────────────────────────────────────────────────────
   */
  const { search } = useLocation();
  const listed = listOriginOf(search);
  const viaRecord = cameViaRecord(search);
  const origin = existing.isLoadingError ? listed : originForOrder(listed, existing.data?.type);

  /**
   * ⚠️ [amended D4] THE SEEDING IS PER CARD NOW, AND BOTH HALVES RUN IN ONE EFFECT.
   *
   * A card with no policy takes the only active one (or the default), and a card whose
   * version has arrived with an EMPTY grid is seeded one requirement line per usable
   * column — because the columns ARE the policy, and asking the operator to re-choose them
   * would make the policy pointless.
   *
   * ⚠️ IT ONLY EVER RUNS ON AN EMPTY GRID, so it cannot overwrite anything typed — the
   * guard that has kept this effect safe since [FIX-1], applied per card rather than once.
   *
   * ⚠️ `postRows`/`vehicleRows`, NEVER the raw arrays: a NEW card is never offered an
   * archived column, while `allPostTypes` still RESOLVES one an existing card already
   * carries. Two different questions, and this is where they part.
   */
  useEffect(() => {
    const values = (form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[];
    if (values.length === 0) return;
    /* ⚠️ [C5c §2] The store, never the draft — the same rule as `dutyPoints` one line up. */
    const shared = (form.getFieldValue('sharedColumns') ?? []) as SharedColumnInputDto[];

    let touched = false;
    let movedSilently = false;

    const next = values.map((point) => {
      /*
       * ─────────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [amended C1f §4] THE DEFAULT-POLICY ARM IS DELETED FROM THIS EFFECT.
       *
       *  It read `if (!card.policyId && fallback) card.policyId = fallback.id`, and this
       *  effect is a REPAIR PASS over every card in the form — so it could not tell a card
       *  the operator had just added from one a SAVED DRAFT had deliberately left blank. A
       *  draft reopened a week later filled its own policy in behind the operator, silently,
       *  and then the columns seeded from it.
       *
       *  ⚠️ THE FEATURE DID NOT MOVE — IT WAS ALREADY IN THE RIGHT PLACE. `addDutyPoint`
       *  seeds a NEW card through `newDutyPoint(paDate, defaultPolicyFor(activePolicies))`
       *  and `duplicateDutyPoint` carries the source's policy in its spread, so every path
       *  that CREATES a card already answers this at the moment of creation. Removing the arm
       *  removes the question rather than answering it, which is why there is no "is this
       *  card new?" predicate anywhere.
       *
       *  ⚠️ AND A CARD WITH NO POLICY STILL SAVES. `dutyPointInputSchema.policyId` is
       *  optional; the card renders no grid and `sections.tsx`'s own `validate` blocks the
       *  publish with "ask for at least one person". That is the honest state, not a hole.
       * ─────────────────────────────────────────────────────────────────────────────────
       */
      let card = point;
      /* ⚠️ Residue from a removed card — `toDraft`'s filter, applied to the store. */
      if (!card?.id) return card;
      const version = versionOf(card);
      /* ⚠️ [C5c §2] A column another card covers is never seeded onto this one. */
      const covering = coveringKeys(shared, card.id);
      if (version && card.requirementLines.length === 0) {
        const columns = resolvePolicyColumns(version);
        const seeded = seededLines(columns, covering);
        /*
         * ⚠️ [C5c §2] A COVERED CARD WHOSE ONLY COLUMN IS SHARED SEEDS TO NOTHING, EVERY TIME. Writing
         * that back would be a no-op the effect repeats on every run; the card is touched only when
         * the seed adds a line or its columns' provenance actually moves.
         */
        if (seeded.length === 0 && card.columnsFromVersionId === version.id) return card;
        /*
         * ⚠️ [amended PRE-P6 §3a] THE COLUMNS ARE SEEDED AND THE VERSION IS **NOT** PINNED.
         *
         * This used to write `policyVersionId: version.id` here, so every new duty point
         * reached the wire naming a version the CLIENT had chosen — read out of a policy
         * list cached for 30s with `refetchOnWindowFocus: false`. A builder tab that was
         * already open when somebody published v2 went on sending v1 for ever, and
         * `resolveDutyPointPolicies` HONOURS a supplied version rather than overriding it,
         * so the server's own "use the current published one" branch was unreachable from
         * this screen — the exact thing its header says must not happen.
         *
         * Omitting the field is what asks the server to resolve it, and the server is the
         * only thing that can answer correctly. The columns still come from the version
         * fetched above; they are a starting grid, not the binding.
         */
        card = { ...card, requirementLines: seeded, columnsFromVersionId: version.id };
        touched = true;
      } else if (
        /*
         * ─────────────────────────────────────────────────────────────────────────────
         *  ⚠️ [C1a §2] THE COLUMNS FOLLOW THE VERSION THIS CARD WILL ACTUALLY BIND.
         *
         *  A card that names no version is saved against whatever the policy's pointer says
         *  AT SAVE TIME. Before this, the columns were frozen at the first seed while the
         *  `v{n}` label re-derived from the pointer on every render — so publishing v2 with
         *  a builder tab open produced a card labelled `v2` showing v1's grid, which is the
         *  reported defect exactly.
         *
         *  ⚠️ `!card.policyVersionId` — UNPINNED ONLY. A stored order is frozen on the
         *  version it was raised against and nothing here may move it ([P2], [FIX-1]).
         *
         *  ⚠️ AND IT STILL DOES NOT WRITE `policyVersionId`. The omission is what asks the
         *  server to resolve, and re-pinning from a client-held list is exactly what
         *  [PRE-P6] §3a deleted. This follows the pointer; it never becomes it.
         * ─────────────────────────────────────────────────────────────────────────────
         */
        version &&
        !card.policyVersionId &&
        card.columnsFromVersionId &&
        card.columnsFromVersionId !== version.id &&
        untouchedGrid(card.requirementLines) &&
        /*
         * ⚠️ [C5c §2] NEVER A SHARED ANCHOR, SILENTLY. Its zero line for a shared column is what the
         * other cards' stashes hang off; a re-seed that dropped it would end the share and discard
         * every number the other cards set aside, with nothing on screen to say so. The moved
         * notice offers it instead, and `useCurrentColumns` confirms first.
         */
        anchoredKeys(shared, card.id).size === 0
      ) {
        card = {
          ...card,
          requirementLines: seededLines(resolvePolicyColumns(version), covering),
          columnsFromVersionId: version.id,
        };
        touched = true;
        movedSilently = true;
      }
      /*
       * ⚠️ [amended PRE-P6 §3a] THE RE-POINT THAT USED TO LIVE HERE IS DELETED.
       *
       * `else if (version && card.policyVersionId !== version.id)` moved a SAVED duty point
       * onto whatever version the cached policy list currently named — including back to v1
       * — every time somebody reopened the draft, with nothing on screen to say so. A
       * version an order was raised against is frozen by design ([P2], defended by [FIX-1]);
       * only the SERVER may choose a version, and only for a duty point that names none.
       */
      return card;
    });

    if (!touched) return;
    /* ⚠️ [C5c §2] Through the one net every card write passes, so a seed cannot break a share. */
    const normalized = normalizeSharing({ cards: next, shared, zeroLineFor });
    form.setFieldsValue({ dutyPoints: normalized.cards, sharedColumns: normalized.shared });
    notifyDevChange(
      movedSilently
        ? {
            where: 'Assign duty',
            what: 'duty point columns re-seeded from the policy`s current version',
            why: 'Nothing had been typed into this grid and the card names no version, so the server will bind the CURRENT one — the columns and the v-label have to name the same version. A grid with anything in it is offered the change instead.',
          }
        : {
            where: 'Assign duty',
            what: 'duty point policy and columns seeded',
            why: 'The columns ARE the policy, and a one-option picker is not a choice. It runs only on an empty grid, so nothing typed can be overwritten.',
          },
    );
  }, [activePolicies, versionOf, zeroLineFor, form, draft.dutyPointIds]);

  // ── Everything the right column and the cards derive from. ───────────────────
  /**
   * ⚠️ ONE MEMO OVER THE WHOLE STACK, NEVER A HOOK PER CARD.
   *
   * `disclosure`, `toGrid` and `generateSlots` are all pure, so they run inside a single
   * `useMemo` here rather than inside the card component — which keeps the hook COUNT fixed
   * whatever the operator adds or removes. The cards are then dumb: everything they render
   * arrives as a prop.
   */
  /*
   * ⚠️ [C3c §9b] THE ORDER IS NO LONGER A DRAFT — read here as well as in `sectionState`, because
   * [C5c §2a] offers the share toggle on a draft only and the memo below decides it.
   */
  const dutyPointsLocked = existing.data !== undefined && existing.data.status !== 'DRAFT';

  const dutyPoints = useMemo<DutyPointView[]>(() => {
    /*
     * ─────────────────────────────────────────────────────────────────────────────────
     *  [C5c §2] TWO PASSES. The first is each card on its own, exactly as before; the second
     *  resolves what sharing means for each card, because a covered card's dash reads the
     *  ANCHOR's column — its label and its count — which only exists once every card is built.
     * ─────────────────────────────────────────────────────────────────────────────────
     */
    const shared = draft.sharedColumns;
    const views = draft.dutyPoints.map((point, index) => {
      const id = draft.dutyPointIds[index] ?? '';
      const covering = coveringKeys(shared, id);
      const schedule = scheduleOfDraft(point);
      let slots: Slot[] = [];
      try {
        if (schedule) slots = generateSlots(schedule as Parameters<typeof generateSlots>[0]);
      } catch {
        /* A half-typed card contributes nothing — and only ITS counts go to zero. */
        slots = [];
      }

      const version = versionOf(point);
      const columns = resolvePolicyColumns(version);
      /*
       * ⚠️ [C1a §2] THE LABEL MAPS ARE THE UNION OF BOTH VERSIONS, BINDING LAST.
       *
       * While a card is mid-divergence its lines carry columns the BINDING version no
       * longer has. Resolving against the binding alone prints `—` / "No longer available"
       * beside numbers somebody typed — so the notice cannot even name what it is about to
       * drop, and a column being renamed flickers to `—` for the length of a fetch.
       *
       * This is `lib/policyColumns.ts`'s own rule one level up: `allPostTypes` RESOLVES,
       * `postRows` OFFERS. Seeding and `forks` below stay on the binding version alone.
       */
      const shown = resolvePolicyColumns(columnsVersionOf(point));
      const grid = toGrid(point.requirementLines, {
        postTypes: mergedLabels(shown.allPostTypes, columns.allPostTypes),
        vehicleTypes: mergedLabels(shown.allVehicleTypes, columns.allVehicleTypes),
      });

      /*
       * ⚠️ §5c — A SUPERSET TEST, NEVER AN EQUALITY TEST. A card using 3 of a policy's 5
       * columns has not changed the policy; only asking for a column the version does not
       * carry does. Ad-hoc lines are invisible to it — FR-5.2.6c says a one-off column
       * never writes back. Getting this wrong would fork a version on every order raised.
       */
      const known = new Set([...columns.allPostTypes.keys(), ...columns.allVehicleTypes.keys()]);
      const forkIds = carriedColumnIds(point.requirementLines).filter((id) => !known.has(id));
      const forks = forkIds.length > 0;
      /*
       * ⚠️ `name`, NOT `shortName`/`code`. The two short forms are different fields on the
       * two summary DTOs — but the deciding reason is that these strings go into a
       * SENTENCE an operator reads. "Unarmed Branch Men" belongs there; "UB" belongs in a
       * column header.
       */
      const nameOf = (id: string): string =>
        mergedLabels(shown.allPostTypes, columns.allPostTypes).get(id)?.name ??
        mergedLabels(shown.allVehicleTypes, columns.allVehicleTypes).get(id)?.name ??
        'a column';

      /*
       * ─────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [C1a §2] THE POLICY MOVED UNDER THIS CARD.
       *
       *  Three guards keep a PINNED card out, and all three are wanted: `!policyVersionId`
       *  here, the same test in the seeding effect, and — once the render path can finally
       *  SEE `policyVersionId` at all — the arithmetic, because a pinned card's binding IS
       *  its columns version and the ids cannot differ.
       *
       *  ⚠️ AND A BUMP THAT CHANGES NO COLUMN SAYS NOTHING. A rename, a reorder or an
       *  edit to the command roles leaves `added` and `dropped` both empty, and there is
       *  nothing for the operator to decide — the memory is simply re-pointed when they
       *  next touch the grid.
       * ─────────────────────────────────────────────────────────────────────────────
       */
      /*
       * ⚠️ [C5c §2] A COVERED COLUMN IS NOT "ADDED". The card carries no line for it by design,
       * so without this every covered card would report the shared column as one the new
       * version adds.
       */
      const offered = new Set([
        /* ⚠️ `lineKeyOf`, never a hand-built `P:`/`V:` string — [C4a §3]'s one spelling. */
        ...columns.postRows.filter((row) => !covering.has(lineKeyOf({ postTypeId: row.id }))).map((row) => row.id),
        ...columns.vehicleRows
          .filter((row) => !covering.has(lineKeyOf({ vehicleTypeId: row.id })))
          .map((row) => row.id),
      ]);
      const carried = new Set(carriedColumnIds(point.requirementLines));
      const dropped = [...carried].filter((id) => !offered.has(id)).map(nameOf);
      const added = [...offered].filter((id) => !carried.has(id)).map(nameOf);
      const versionMoved =
        version &&
        !point.policyVersionId &&
        point.columnsFromVersionId &&
        point.columnsFromVersionId !== version.id &&
        added.length + dropped.length > 0
          ? { label: `v${String(version.versionNumber)}`, added, dropped }
          : undefined;

      return {
        id,
        index,
        draft: point,
        shape: disclosure({ schedule, commandEntries: point.commandEntries }),
        slots,
        grid,
        roles: version?.commandRoles ?? [],
        policyName: activePolicies.find((row) => row.id === point.policyId)?.name ?? '—',
        /* ⚠️ [PRE-P6 §3a] The version the SERVER will bind, which is this card's own when
             it has one and the policy's current published one when it does not. */
        policyVersionLabel: version ? `v${String(version.versionNumber)}` : null,
        forks,
        forkColumns: forkIds.map(nameOf),
        /*
         * ⚠️ [C1c §4a] `allPostTypes`, NEVER `postRows` — `policyColumns.ts`'s two answers.
         *
         * A restriction saved last month may name a post type since archived, and this list
         * is what renders the chip for it. Filtering to what is currently choosable would
         * make that saved value show as blank, which is the silent regression that file's
         * header warns about one field over.
         */
        postTypeOptions: [...mergedLabels(shown.allPostTypes, columns.allPostTypes).values()].map((row) => ({
          id: row.id,
          name: row.name,
        })),
        versionMoved,
        open: openCards.includes(id),
        coveredColumns: [] as CoveredColumn[],
        sharedLineIndexes: new Set<number>(),
        /* §2a — more than one card, a draft, a writer, and a version to converge on. */
        shareOffered: !readOnly && !dutyPointsLocked && draft.dutyPoints.length > 1 && version !== undefined,
        policyLockedBySharing: shared.length > 0,
      };
    });

    if (shared.length === 0) return views;
    return views.map((view) => {
      const anchored = anchoredKeys(shared, view.id);
      const sharedLineIndexes = new Set(
        view.draft.requirementLines.flatMap((line, lineIndex) => (anchored.has(lineKeyOf(line)) ? [lineIndex] : [])),
      );
      /*
       * ⚠️ THE ANCHOR'S OWN COLUMN, FOUND BY KEY — never by position, which is the anchor's rule one
       * level down. An entry whose anchor or line is gone renders nothing here; `normalizeSharing`
       * ends it on the next write.
       */
      const coveredColumns = shared.flatMap((entry): CoveredColumn[] => {
        if (entry.anchorDutyPointId === view.id) return [];
        const anchor = views.find((row) => row.id === entry.anchorDutyPointId);
        const column = anchor?.grid.columns.find(
          (row) => lineKeyOf(anchor.draft.requirementLines[row.lineIndex] ?? {}) === entry.lineKey,
        );
        if (!anchor || !column) return [];
        return [
          {
            lineKey: entry.lineKey,
            kind: column.kind,
            label: column.label,
            fullLabel: column.fullLabel,
            anchorLabel: `DP${String(anchor.index + 1)}`,
            anchorCount: anchor.grid.totals[column.lineIndex] ?? 0,
          },
        ];
      });
      return { ...view, sharedLineIndexes, coveredColumns };
    });
  }, [
    draft.dutyPoints,
    draft.dutyPointIds,
    draft.sharedColumns,
    versionOf,
    columnsVersionOf,
    activePolicies,
    openCards,
    readOnly,
    dutyPointsLocked,
  ]);

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [D4.4 §1b] THIS WAS A PERMANENTLY EMPTY MAP, AND IT HAD BEEN SINCE FIX-1.
   *
   *  `new Map()` with an empty dependency array is a map that never fills, so FIX-1's
   *  "the grid resolves the names once and the cells render immediately" optimisation
   *  never fired, every source cell's `aria-label` read "New source" or "this source"
   *  instead of naming a division, and `divisions` below fell through to its own
   *  fallback — producing summaries whose `name` is a raw 24-character ObjectId and
   *  whose `shortCode` is the empty string. The right rail then counted them correctly
   *  and named them unreadably.
   *
   *  ⚠️ IT COSTS NO REQUEST BEYOND THE TREE'S OWN. `queryKeys.orgUnits.tree()` is the key
   *  `SourceCell` already uses, so this page and every source cell on it share one cache
   *  entry — which is also why the map arrives already warm on a card the operator has
   *  touched.
   *
   *  ⚠️ [amended OTHERS-EXPAND] AND "THE TREE'S OWN" IS NO LONGER ONE REQUEST. The scoped tree
   *  outgrew one 500-node page once sections were added, so the shared entry is filled by
   *  `fetchScopedOrgTree` — one request per page, at most `ORG_TREE_MAX_PAGES` — and this page
   *  must use that fetcher too: a shared key with two `queryFn`s is truncated or not depending
   *  on which one ran first. What is still true is that the page and its cells pay for it ONCE.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const orgUnitTree = useQuery({
    queryKey: queryKeys.orgUnits.tree(),
    queryFn: ({ signal }) => fetchScopedOrgTree(signal),
    staleTime: 5 * 60_000,
  });

  const orgUnits = useMemo(
    () =>
      new Map<string, OrgUnitSummaryDto>(
        (orgUnitTree.data?.items ?? []).map((unit) => [
          unit.id,
          { id: unit.id, name: unit.name, shortCode: unit.shortCode, type: unit.type, subType: unit.subType },
        ]),
      ),
    [orgUnitTree.data],
  );

  const divisions = useMemo(
    () =>
      [...new Set(dutyPoints.flatMap((point) => point.grid.sources.map((source) => source.divisionOrgUnitId)))]
        .filter((id) => id !== '')
        /* The fallback survives for a division outside the caller's scope; it is no longer
           the ordinary path, which is what made an ObjectId reach the screen. */
        .map<OrgUnitSummaryDto>(
          (id) => orgUnits.get(id) ?? { id, name: id, shortCode: '', type: 'DIVISION', subType: 'OTHER' },
        ),
    [dutyPoints, orgUnits],
  );

  /* ⚠️ A SUM ACROSS CARDS, NEVER A PRODUCT — §3, and the same arithmetic the server does. */
  const deploymentCount = dutyPoints.reduce((total, point) => total + point.slots.length, 0);
  const slotCount = new Set(dutyPoints.flatMap((point) => point.slots.map((slot) => slot.slotKey))).size;
  const requirementTotals = dutyPoints.flatMap((point) => point.grid.totals);

  /* The title derives from the FIRST card's places — `arrangementTitle.util.ts` unchanged. */
  const titleParts = {
    locationNames: dutyPoints.flatMap((point) => point.draft.locationNames),
    paDate: draft.paDate,
    firstStartTime:
      dutyPoints[0]?.draft.timingKind === 'NO_FIXED_TIME' ? null : dutyPoints[0]?.draft.shifts[0]?.startTime,
  };
  const derivedTitle = deriveArrangementTitle(titleParts);
  const displayTitle = arrangementDisplayTitle(draft.title, titleParts, existing.data?.reference ?? 'New arrangement');

  // ── Autosave, from the first keystroke. ───────────────────────────────────────
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      savedId
        ? api.patch<ArrangementDto>(`/arrangements/${savedId}`, body)
        : api.post<ArrangementDto>('/arrangements', body),
    onSuccess: (saved) => {
      /*
       * ⚠️ [C3a §7d] ONE WRITER — see `markClean`. The three facts cannot drift apart.
       * ⚠️ AND IT CLEARS ONLY WHAT THIS WRITE ACTUALLY CARRIED — see `editSeq`. An edit that
       * arrived mid-flight leaves the form dirty, so `onSettled`'s queued re-flush still has the
       * `dirtyRef.current` it tests and the work reaches the server on the next round trip.
       */
      if (editSeq.current === sentSeq.current) markClean('saved');
      else writeSaveState('unsaved');
      if (!savedId) {
        setSavedId(saved.id);
        seededFor.current = saved.id;
        /*
         * ─────────────────────────────────────────────────────────────────────────────
         *  ⚠️ [amended C1f §6] `history.replaceState`, NOT `navigate` — AND THIS IS THE
         *  DIFFERENCE BETWEEN AUTOSAVE AND A DATA-LOSS BUG.
         *
         *  `/arrangements/new` and `/arrangements/:arrangementId/edit` are two ROUTES, so
         *  `navigate` between them REMOUNTS this page. That was tolerable when the first save
         *  was a deliberate click on `Save draft`: it happened once, at a moment the operator
         *  chose. Under §6 it happens on the FIRST BLUR — mid-form, while they are still
         *  typing — and the remounted page re-seeds from `existing`, whose copy has been
         *  through `submittableLines`. A division-less source row and a blank place row
         *  disappear. That is [P3b.3]'s loss exactly, on the one path its own guard never
         *  covered, and autosave would have made it routine rather than rare.
         *
         *  ⚠️ SO THE COMPONENT DOES NOT UNMOUNT AT ALL. `savedId` is state and is what every
         *  subsequent PATCH reads; `useParams` stays undefined, so `existing` stays DISABLED
         *  and there is no refetch to re-seed from. The form in front of the operator remains
         *  the truth for the whole sitting, which is what [P3b.3] was protecting.
         *
         *  ⚠️ AND THE URL IS STILL CORRECT AND STILL SHAREABLE. `replaceState` adds no history
         *  entry, so backing out of a half-filled form still does not litter the list —
         *  `PolicyBuilder`'s reason for `{ replace: true }`, kept.
         *
         *  ⚠️ THE COST, STATED: React Router's own location is stale until the next real
         *  navigation, which on this page is the publish or the discard. Nothing here reads
         *  it — `savedId` does the work — and `window.history.replaceState` is already this
         *  repository's idiom for exactly this (`useUnsavedGuard`, and every builder test's
         *  `beforeEach`).
         *
         *  ⚠️ [AUTO-SAVE] AND THE ENTRY'S OWN STATE IS KEPT, NEVER NULLED. It carries React Router's
         *  key and index and, while the leave guard is armed, the guard's marker — and with
         *  `ENABLE_AUTO_SAVE=false` the guard is armed at this very moment (the Save press is what
         *  got here). Nulled, the guard re-armed on the next edit and pushed a second Back entry,
         *  so "Leave" landed on the first one and the operator was asked twice.
         * ─────────────────────────────────────────────────────────────────────────────
         */
        window.history.replaceState(window.history.state, '', `/arrangements/${saved.id}/edit`);
      }
      /*
       * ─────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [C1a §2] THE VERSION THE SERVER CHOSE, WRITTEN BACK — NOT ONE THE CLIENT PICKED.
       *
       *  [PRE-P6] §3a removed the client PINNING a version out of a 30-second-stale list.
       *  This is the opposite direction and is safe for that reason: the server has now
       *  RESOLVED and STORED one, and this is the card learning what it was. Without it a
       *  saved card keeps `policyVersionId: undefined` in the form store, so its label goes
       *  on floating on the policy pointer while its columns stay put — the same
       *  label-says-v2 / grid-says-v1 divergence, on a record that is no longer free to move.
       *
       *  ⚠️ MATCHED BY THE MINTED `id`, NEVER BY INDEX — it round-trips, so this survives a
       *  reorder in the same save.
       *
       *  ⚠️ `setFieldValue`, NOT `editCards`: it fires no `onValuesChange`, so the form does
       *  not go dirty a line after `setDirty(false)`. And it costs no request — the version
       *  it names is the cache entry already on screen.
       *
       *  ⚠️ NOT the `existing` effect. Its `seededFor` guard is the fix for a data-loss bug
       *  (the server's copy has been through `submittableLines`, so re-hydrating mid-typing
       *  snaps a half-typed number back), and relaxing it by any amount reintroduces that.
       * ─────────────────────────────────────────────────────────────────────────────
       */
      const bound = new Map(saved.dutyPoints.map((point) => [point.id, point.policyVersionId]));
      const cards = (form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[];
      let changed = false;
      const backFilled = cards.map((card) => {
        const versionId = bound.get(card.id);
        if (!versionId || card.policyVersionId === versionId) return card;
        changed = true;
        return { ...card, policyVersionId: versionId, columnsFromVersionId: card.columnsFromVersionId ?? versionId };
      });
      if (changed) form.setFieldValue('dutyPoints', backFilled);

      /*
       * ─────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [amended C1f §6] `setQueryData`, AND **NOT** `arrangements.all`.
       *
       *  That invalidation is [P3b.3]'s exact bug: `arrangements.all` is `['arrangements']`,
       *  a PREFIX of `detail(id)`, so every save refetched the record being edited and the
       *  seeding effect wrote the server's stripped copy back over the form. Under a
       *  deliberate click the `seededFor` ref happened to absorb it; under autosave it is a
       *  race nobody should be relying on.
       *
       *  What is left is safe by shape rather than by luck: `lists()` is a SIBLING of
       *  `detail`, and `publishPreview(id)` is a strict DESCENDANT of it — refetching a
       *  child cannot refetch its parent — and it is what makes §7's control leave the
       *  blocked state without a reload.
       * ─────────────────────────────────────────────────────────────────────────────
       */
      queryClient.setQueryData(queryKeys.arrangements.detail(saved.id), saved);
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.publishPreview(saved.id) });
    },
    /*
     * ⚠️ A SILENT AUTOSAVE FAILURE LOSES AN OPERATOR'S WORK, which is the worst bug this
     * page could ship. The indicator goes to a state that says what to DO, and the form
     * stays dirty so the guard still fires on the way out.
     */
    onError: (error: ApiError) => {
      writeSaveState('failed');
      /*
       * ⚠️ [C5c §8] A SAVE THAT FINDS THE DRAFT GONE SAYS WHY — BUT ONLY WHEN THE DRAFT'S OWN CLOCK
       * SAYS IT WAS DUE. The thirty-day sweep may delete a draft that is open here, and a bare
       * "Arrangement not found" reads as a fault. A 404 also follows a scope change, though, and the
       * API collection's rule is that a 404 is never rendered as "deleted" on a guess — so the code
       * is switched on, never the status, and the cached record must be past its thirty days.
       * The indicator stays `failed` and the leave guard still fires: nothing here pretends it saved.
       */
      const record = savedId
        ? queryClient.getQueryData<ArrangementDto>(queryKeys.arrangements.detail(savedId))
        : undefined;
      const text =
        error.code === 'NOT_FOUND' && record !== undefined && draftHasExpired(record, new Date())
          ? DRAFT_EXPIRY_COPY.gone
          : error.message;
      /*
       * ⚠️ [C1f §6] ONE TOAST PER **DISTINCT** MESSAGE.
       *
       * `assertColumnsBelongToVersion` 422s a card carrying a column its bound version does
       * not have, and that refusal does not clear until the operator presses "Update the
       * columns" — so under blur-frequency writes every field they left in between would
       * raise the same sentence again. The INDICATOR is the standing channel; the toast is
       * the announcement, and an announcement repeated is noise.
       *
       * That service's own header refused autosave on exactly this ground ("against the old
       * keystroke-frequency PATCH this would have been a lockout"). Blur frequency plus this
       * latch is the answer, and the button that fixes it is on the same screen.
       */
      if (lastError.current !== text) {
        lastError.current = text;
        void message.error(text);
      }
    },
    /*
     * ⚠️ [C1f §6] THE LATCH RELEASES HERE, AND A QUEUED TRIGGER DRAINS.
     *
     * `onSettled` rather than `onSuccess`, or a refused write would leave the page unable to
     * try again — which is the one failure mode an autosaving form must not have.
     */
    onSettled: () => {
      inFlight.current = false;
      if (queued.current && dirtyRef.current) {
        queued.current = false;
        /* ⚠️ [AUTO-SAVE] With autosave off the only trigger that can have queued is the Save button
           (every other one stops before the queue), so it is replayed as itself, never as a blur. */
        flushRef.current(autoSave ? 'blur' : 'save-button');
      }
    },
  });

  const body = useCallback((): Record<string, unknown> => {
    /*
     * `getFieldsValue(true)` is typed `any` by rc-field-form, so the annotation alone is
     * an unsafe assignment. One cast, here, at the single point the form store leaves the
     * form — every other read in this page goes through the watched draft, which IS typed.
     */
    const values = form.getFieldsValue(true) as ArrangementFormValues;
    return {
      paDate: values.paDate?.toISODate(),
      paNumber: values.paNumber ?? null,
      // ⚠️ NULL WHEN DERIVED. A stored derived title goes stale the moment a place changes.
      title: values.title?.trim() ? values.title.trim() : null,
      description: values.description ?? null,
      estimatedCrowdSize: values.estimatedCrowdSize ?? null,
      /* [C1b §4] Visibility only. `?? []` because a form that never registered it means none. */
      associatedOrgUnitIds: values.associatedOrgUnitIds ?? [],
      /*
       * ⚠️ EVERY CARD, THROUGH THE ONE CROSSING. `toDutyPointInput` is where a card's flat
       * form fields become the wire's discriminated schedule, and `submittableLines` strips
       * a zero cell and a source with no division chosen — at SUBMIT only, so a row being
       * typed into survives on screen. Sending a partial list would DELETE the cards it
       * omits, because the server replaces the whole array.
       */
      /* ⚠️ GHOSTS FILTERED HERE TOO — see `toDraft`. A residue card would reach the wire
         as a duty point with no place and no policy and fail validation on save. */
      dutyPoints: (values.dutyPoints ?? [])
        .filter((point) => Boolean(point?.id))
        .map((point) => toDutyPointInput(point, values.paDate)),
      /*
       * ⚠️ [C5c §2] ALWAYS SENT, `[]` WHEN NOTHING IS SHARED — the server replaces the whole list,
       * so omitting it would be read as "no change" on a PATCH and a stale share would survive.
       * The stash goes through `submittableLines`' rule too, or one unpicked "Add source" row on a
       * card that set its number aside 422s every autosave until sharing is switched off.
       */
      sharedColumns: submittableSharedColumns(values.sharedColumns ?? []),
      /*
       * ⚠️ [C5c §8e] THROUGH THE ARMING CHECK'S OWN PREDICATES, so what arms a save and what the save
       * sends cannot disagree. The people were sent UNFILTERED here while a card's were filtered, so
       * the order-level "Add person" 422'd every save until the row was named or removed.
       */
      instructions: values.instructions.filter(instructionIsReal),
      supportingArrangements: values.supportingArrangements.filter(supportingPersonIsReal),
    };
  }, [form]);

  /** ⚠️ What publishing will create, asked BEFORE it happens — never a bare "Are you sure?". */
  const preview = useQuery({
    queryKey: queryKeys.arrangements.publishPreview(savedId ?? ''),
    queryFn: ({ signal }) => api.get<PublishPreviewDto>(`/arrangements/${savedId ?? ''}/publish-preview`, { signal }),
    enabled: Boolean(savedId),
  });

  const [discardOpen, setDiscardOpen] = useState(false);

  /**
   * ⚠️ [D4.5 §2a] HIDE-THEN-SHOW, NEVER SHOW-THEN-HIDE.
   *
   * On the edit route the status is not known until `existing` resolves, and offering a
   * destructive action and then withdrawing it is worse than offering it a beat late. So an
   * unresolved record is treated as NOT a draft; the create route has no id to fetch and is
   * a draft by definition.
   *
   * A PUBLISHED order has no Discard here at all: archiving one is a CANCELLATION that emits
   * events and states an officer count first, and `pages/ArrangementDetail` already owns
   * that act with its own confirmation. Two doors onto one destructive action, phrased
   * differently, is what [LIFECYCLE] built `ConfirmDestructive` to stop.
   */
  const isDraft = existing.data ? existing.data.status === 'DRAFT' : !arrangementId;

  /*
   * ⚠️ [C5c §8] THE THIRTY-DAY SWEEP'S DATE FOR THIS DRAFT, read off the stored record in render — the
   * same clock read `identityLocked` makes below. `null` for a record that will never be swept and for
   * the create route, whose draft was started this sitting.
   */
  const expiryNotice = existing.data ? draftExpiryNotice(existing.data, new Date()) : null;

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [D4.5 §2a] `Discard` ARCHIVES THE DRAFT. IT IS THE SOFT PATH, NOT A DELETE.
   *
   *  [LIFECYCLE]'s axis: `archived_at` plus a `purge_at` thirty days out, restorable from
   *  the Archived view — and, since [C5c §8], deleted by the duty-lifecycle tick when that
   *  `purge_at` passes. The endpoint and the transaction already existed — this page simply
   *  had no control that reached them, so a half-built order could only be abandoned by
   *  navigating away and leaving it in the list for ever.
   *
   *  ⚠️ IT DOES NOT FETCH `delete-impact`, AND `pages/ArrangementDetail` IS RIGHT TO.
   *  That page archives PUBLISHED orders, where the counts are the whole point of stating
   *  the cost before the click. A DRAFT has no deployments — they are created at publish —
   *  so the impact is `0 / 0 / false / false` BY CONSTRUCTION, and the dialog would spend a
   *  round trip to say "0 duties will be withdrawn". The guard is not the client fetch in
   *  any case: `archiveArrangement` re-checks both refusals INSIDE the transaction, because
   *  `hasOpenedWindow` flips on its own.
   *
   *  ⚠️ NOTHING SAVED MEANS NOTHING TO ARCHIVE. One button, two confirmation bodies, and
   *  the branch is here rather than at the call site so the bar reads the same either way.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const leaveAfterDiscard = (): void => {
    /* ⚠️ THE GUARD IS DISARMED FIRST — publish's argument verbatim. `setDirty` is a state
       write the guard reads on the NEXT render, so this ordering is what makes a deliberate
       discard navigate silently instead of raising "you have unsaved changes". Do not tidy
       it into the other order. */
    /* ⚠️ [amended C2a §2] The ref goes too — see publish's note. A discard that left it set
       fired an unmount autosave that re-created the very draft the operator had just archived.
       ⚠️ [amended C3a §7d] AND `saveState` WITH THEM: leaving with it stale would render a
       `Save` control on the way out, for the tick before the navigation lands. */
    markClean('idle');
    /* ⚠️ [TYPED-BACK] THE LIST IT CAME FROM, else Manage Arrangements — `listAfterLeaving`. Only the
       DESTINATION moved: the disarming above still runs first. `origin` is already checked against
       the loaded record's type, so an untyped draft is never sent to a typed list. ⚠️ [amended
       TYPE-FILTER] With no origin a typed draft went to its own type's list, because Manage
       Arrangements did not list one; it does now, so it lands there like every other order.
       ⚠️ [DP2026] Except a draft made from its own type's booklet, which Manage Arrangements does not list:
       it goes to its type's list (`homeListOf`). */
    void navigate(listAfterLeaving(origin ?? homeListOf(existing.data)));
  };

  const discard = useMutation({
    mutationFn: () => api.post<ArrangementDto>(`/arrangements/${savedId ?? ''}/archive`),
    onSuccess: () => {
      void message.success('Moved to the Archive');
      /*
       * ⚠️ [amended C1f §6] `lists()` AND THE RECORD, NEVER `arrangements.all`.
       *
       * Both are terminal — this handler navigates away in its next line — so the blunt key
       * cost nothing here. It is narrowed anyway, because `saveKeyIsolation.test.ts` holds the
       * rule ABSOLUTELY over this file, and a grep-as-test with an exception is a grep-as-test
       * somebody adds a second exception to. It is also strictly more precise: `all` sweeps
       * every arrangement's duties, preview and publish preview as well.
       */
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.lists() });
      if (savedId) void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.detail(savedId) });
      leaveAfterDiscard();
    },
    onError: (error: ApiError) => {
      setDiscardOpen(false);
      void message.error(error.message);
    },
  });

  const publish = useMutation({
    mutationFn: (intent: 'REQUEST_DIVISIONS' | 'ASSIGN_MYSELF') =>
      api.post<ArrangementDto>(`/arrangements/${savedId ?? ''}/publish`, { intent }),
    onSuccess: (published) => {
      void message.success(`${published.reference} published`);
      /* ⚠️ [amended C1f §6] The list, and this record — whose duties and previews are its
         DESCENDANTS and go with it. See the discard handler above for why not `all`. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.detail(published.id) });
      /*
       * ⚠️ [P4] `ASSIGN_MYSELF` KEEPS THE NAMING WITH THIS OFFICE, so the operator is taken
       * to the work rather than to a list of what they just created. `REQUEST_DIVISIONS`
       * hands it to the divisions and stays where it is — the difference between the two
       * buttons was always "what happens next", and now something does.
       *
       * ─────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [D4.4 §4] BOTH BUTTONS NOW LEAVE THE FORM. `REQUEST_DIVISIONS` STAYING PUT WAS
       *  A DEAD END, NOT A DECISION.
       *
       *  It left the operator on a builder for an order they could no longer edit, with no
       *  control on the page that went anywhere — the reported "no manual back". It goes to
       *  the LIST, where the order they just issued is visible among the others.
       *
       *  ⚠️ `ASSIGN_MYSELF` STILL GOES TO THE RECORD, and that is [P4]'s decision left
       *  standing rather than an omission: the operator who chose to name officers
       *  themselves asked to be taken to the work, and sending them to a list they must
       *  then click back out of would undo the whole point of the two buttons differing.
       *
       *  ⚠️ THE GUARD IS DISARMED FIRST. `useUnsavedGuard` intercepts navigation while
       *  `dirty` is set, and a publish is the least likely moment to want a "you have
       *  unsaved changes" dialog — the server has just accepted everything. `setDirty` is a
       *  state write, so it would not take effect before the `navigate` on this same tick;
       *  the guard reads it on the NEXT render, which is why the order here is safe and why
       *  this comment exists rather than a re-ordering somebody will later "tidy".
       * ─────────────────────────────────────────────────────────────────────────────
       */
      /* ⚠️ [amended C3a §7d] ONE WRITER, and it clears `saveState` too — a published record
         must not render a `Save` control while the navigation resolves. */
      markClean('idle');
      /*
       * ⚠️ [amended C1f §7] ONE DESTINATION, BECAUSE THERE IS ONE BUTTON. `Assign names
       * myself` used to land the operator on the record; publishing now always returns them
       * to the list, and the naming screen is reached from the control's `published` state —
       * which is where an operator arriving fresh finds it too, rather than only in the few
       * seconds after their own click.
       *
       * ⚠️ THE INTENT ITSELF IS UNTOUCHED ON THE WIRE. `setup.spec.ts` posts `ASSIGN_MYSELF`
       * at six places to build the fixtures every other e2e project reads, the standing-order
       * generator passes the other through the same body, and the value is recorded in the
       * `ARRANGEMENT_PUBLISHED` audit context. Removing a button is not removing an API.
       */
      /*
       * ─────────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [amended C2a §2] THE **REF**, NOT ONLY THE STATE — THIS IS THE PUBLISH DATA LOSS.
       *
       *  `setDirty` is React state and `useUnsavedGuard` reads it. AUTOSAVE READS `dirtyRef`
       *  (`armingOf({ dirty: dirtyRef.current })`), and until this line the ref was cleared at
       *  exactly ONE site — `save.onSuccess`. So every successful publish ran:
       *
       *      publish OK → setDirty(false) → navigate() → unmount → flush('unmount')
       *                 → armingOf({ dirty: true }) → 'armed' → PATCH /arrangements/:id
       *
       *  …a full-record PATCH fired at an order that had just been PUBLISHED. `updateArrangement`
       *  had no status guard, so it was ACCEPTED: `applyArrangementContent` replaced
       *  `duty_points` wholesale while the `Deployment` rows stamped milliseconds earlier at
       *  publish kept their own copy, and the order's template silently diverged from its issued
       *  duties until somebody pressed "Update duties". When it 409'd instead, the page had
       *  already unmounted and the toast landed on a dead screen. Either way: silent.
       *
       *  ⚠️ IT IS CLEARED IN THE SAME FUNCTION AS THE STATE, UNCONDITIONALLY — CLAUDE.md §8's own
       *  rule for this class ("the reset belongs to the commit handler and to the dismissal
       *  handler, in the same function"). Never an effect: a reset is a consequence of an
       *  interaction, not a synchronisation.
       *
       *  ⚠️ AND THE SERVER NOW REFUSES IT ANYWAY — [C2a §3] put `assertMayAuthorArrangement` on
       *  `updateArrangement`. Two independent defences, because this one is invisible when it
       *  fails.
       * ─────────────────────────────────────────────────────────────────────────────────
       */
      /* ⚠️ [TYPED-BACK] "THE LIST" IS THE ONE IT WAS OPENED FROM — `listAfterLeaving`, Discard's rule,
         with the same type-checked `origin` (a draft first saved this sitting keeps none). Only the
         destination moved; `markClean` above still runs first. ⚠️ [amended TYPE-FILTER] With no origin
         it is Manage Arrangements whatever the type, which now lists every order. */
      void navigate(listAfterLeaving(origin));
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  /**
   * ⚠️ [amended C1f §6] AUTOSAVE IS BACK, AND `autosave.ts` CARRIES THE WHOLE ARGUMENT.
   *
   * [P3b.3] removed a debounce that wrote 800 ms after a KEYSTROKE, and the loss it caused
   * had two halves: the write invalidated `queryKeys.arrangements.all` — a PREFIX of
   * `detail(id)` — so it refetched the open record, and the seeding effect then wrote the
   * server's stripped copy back over the form. Both halves are gone: the response is written
   * with `setQueryData` and nothing on this page names `arrangements.all`, and the triggers
   * are BLUR, STEP and UNMOUNT rather than a timer.
   *
   * `onValuesChange` still does one thing: remember that there is something to save.
   */
  /**
   * ─────────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3a §7d] THE ONE PLACE THE SAVE FACTS ARE WRITTEN — AND THAT IS WHAT MAKES §7 SAFE.
   *
   *  Three facts describe one save: `saveState` (which the INDICATOR and now `publishControlState`
   *  read), `dirty` (which `useUnsavedGuard`'s unload arm reads) and `dirtyRef` (which `armingOf`
   *  reads, because the blur handler runs before React has re-rendered). [C2a]'s data loss was
   *  those three kept in step by hand across four sites, with one of them forgotten — publish
   *  cleared the state while autosave read the ref, and an unmount fired a PATCH at a published
   *  order.
   *
   *  §7 gates PUBLISH on `saveState`, which makes a fifth reader of the same fact — so the
   *  answer is [C2b §8c]'s: ONE local writer, and a sixth reader inherits it rather than having
   *  to remember. `saveKeyIsolation.test.ts` asserts the counts are EXACT rather than "at least",
   *  so a second writer fails the guard instead of drifting.
   *
   *  ⚠️ `markClean` TAKES ITS NEXT STATE, because the three callers mean different things:
   *  a landed save is `saved`, and publish and discard are leaving the page with nothing owed.
   * ─────────────────────────────────────────────────────────────────────────────────────
   */
  /*
   * ─────────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3a §7d] AN EDIT MADE **DURING** AN IN-FLIGHT SAVE MUST NOT BE CLEARED BY THAT SAVE'S
   *  OWN SUCCESS — AND IT WAS, SILENTLY, WITH THE INDICATOR READING "Saved".
   *
   *  `onSuccess` calls `markClean('saved')`, which clears `dirtyRef`; `onSettled` then re-flushes
   *  a queued trigger only `if (queued.current && dirtyRef.current)`. TanStack runs `onSuccess`
   *  FIRST, so the conjunct it tests has already been falsified by the line above it and the
   *  queued flush never fires. The operator types while a save is in flight, blurs, the save
   *  lands, the badge says **Saved**, and their edit is on nobody's disk.
   *
   *  ⚠️ THE SEQUENCE, NOT A SECOND BOOLEAN. `markClean` means "the server now has what the form
   *  held **when this body was built**" — so the question is whether anything changed since, and
   *  a counter answers it exactly where a flag cannot: an edit that never blurred sets `dirtyRef`
   *  without ever setting `queued`, so `queued` alone still loses it.
   *
   *  ⚠️ AND `markClean` IS STILL THE ONE WRITER. This decides WHETHER to call it, never what it
   *  writes — `saveKeyIsolation.test.ts`'s exact counts are untouched.
   * ─────────────────────────────────────────────────────────────────────────────────────
   */
  const editSeq = useRef(0);
  const sentSeq = useRef(-1);

  const markDirty = (): void => {
    editSeq.current += 1;
    /*
     * ─────────────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [C3a §7b] `unsaved` IS A CLAIM ABOUT CONTENT, SO IT IS TESTED RATHER THAN ASSUMED.
     *
     *  `onValuesChange` cannot tell a STRUCTURAL change from typed content — `autosave.ts` says
     *  so — and `Add duty point` is the structural one: `newDutyPoint()` produces a blank place
     *  row, empty command entries and lines seeded at zero, all refused BY VALUE by
     *  `dutyPointHasContent`. Set unconditionally, the badge read "Not saved", Save rendered
     *  ENABLED, `publishControlState` withdrew Publish at the same instant, and pressing Save
     *  issued nothing: one enabled, inert control, which is the hole [C1f §7b] forbids.
     *
     *  ⚠️ THE FLUSH-TIME FIX ALONE IS NOT ENOUGH, AND THAT WAS MEASURED. Resolving `no-content`
     *  to `idle` inside `flush` is correct and covers the CLICK — but nothing flushes between
     *  the structural change and the next blur, so the button sat wrongly enabled for the whole
     *  of that window. Both halves ship.
     *
     *  ⚠️ IT READS THE FORM STORE, NEVER THE WATCHED `draft`. That one is a lossy PROJECTION
     *  ([C1a], divergence #289) and would fail CLOSED on real content — a field it does not
     *  carry would make genuine work look like nothing worth saving, which is the direction that
     *  loses data rather than the one that shows a spare button.
     *
     *  ⚠️ AND `dirty`/`dirtyRef` ARE UNTOUCHED BY THIS BRANCH. They mean "the form differs from
     *  the server", which is still true of an added card; only the OFFER changes. `armingOf`
     *  checks `dirty` before `no-content` and would otherwise answer `nothing-to-save`, which is
     *  a different sentence about a different fact.
     * ─────────────────────────────────────────────────────────────────────────────────────
     */
    /*
     * ⚠️ ONLY WHILE THE ANSWER CAN CHANGE — `getFieldsValue(true)` ON EVERY KEYSTROKE IS A
     * MEASURED COST, NOT A THEORETICAL ONE. Run unconditionally it took the builder's pinned
     * click-budget test from comfortably inside its 20s to 11s in isolation and a TIMEOUT under
     * full-suite load. `onValuesChange` fires per character on a form that can hold 24 cards.
     *
     * ⚠️ THE NARROWING IS SOUND BECAUSE TYPING ONLY EVER **ADDS** CONTENT. Once the answer is
     * "there is something to save" no further keystroke can make it false, so the check is needed
     * exactly on the transition INTO that claim — at most once per sitting. The opposite move,
     * an operator CLEARING every field back to nothing, is resolved by `flush`'s own
     * `no-content → markClean('idle')` arm, which runs on the very next blur. Both halves are
     * required and neither is sufficient: this one covers the window before a flush, that one
     * covers deletion.
     *
     * ⚠️ A REF, NOT THE RENDER CLOSURE'S `saveState`. A burst of `onValuesChange` calls inside
     * one tick would all read the same stale value — `dirtyRef`'s reason, one field over.
     */
    if (saveStateRef.current === 'idle' || saveStateRef.current === 'saved') {
      const values = form.getFieldsValue(true) as Record<string, unknown> & { dutyPoints?: DutyPointFormValue[] };
      writeSaveState(
        hasMeaningfulContent({
          pa: values,
          dutyPoints: values.dutyPoints ?? [],
          pendingAttachments: pendingRef.current.length,
        })
          ? 'unsaved'
          : 'idle',
      );
    }
    setDirty(true);
    dirtyRef.current = true;
  };

  /*
   * ⚠️ [C3a §3] THE ONE WRITER OF THE PENDING QUEUE — `markDirty`/`markClean`'s shape, and for
   * the same reason: two facts that must never disagree get one function, not a convention.
   */
  const writePending = (next: readonly PendingAttachment[]): void => {
    pendingRef.current = next;
    setPendingAttachments(next);
  };

  const markClean = (next: 'saved' | 'idle'): void => {
    writeSaveState(next);
    setDirty(false);
    dirtyRef.current = false;
    lastError.current = null;
  };

  const onValuesChange = (): void => {
    if (readOnly || !hydrated.current) return;
    markDirty();
  };

  /**
   * ⚠️ [C1f §6] THE ONE WRITER, AND EVERY TRIGGER GOES THROUGH IT.
   *
   * `armingOf` is pure and exhaustive, so "why did that not save?" always has exactly one
   * answer — and the `no-date` answer reaches the operator through the indicator rather than
   * through a toast on every blur.
   *
   * ⚠️ `dirtyRef`, NOT `dirty`. The blur handler is an ancestor of the field that just
   * committed, so it runs before React has re-rendered with the new state.
   */
  const flush = (trigger: SaveTrigger): void => {
    /*
     * ⚠️ [UX1.2] READ ONCE, HERE, AND FROM THE FORM RATHER THAN FROM THE WATCHED DRAFT.
     *
     * The watched draft would have been the wrong source twice over: it is a PROJECTION, so a
     * field it does not carry is invisible to a derivation reading it — [C1a]'s
     * `policyVersionId` defect verbatim — and it is a render-time value, while this runs
     * inside a `focusout` before React has re-rendered.
     *
     * ─── ⚠️ [amended C2b §8c] `getFieldsValue(true)`, AND THE ARGUMENT IS THE UNMOUNT TRIGGER ───
     *
     * It was `getFieldsValue()` — REGISTERED fields — on the correct reasoning that a hidden
     * step is CLASS-hidden rather than unmounted, so tab 1 stays registered while tab 2 is on
     * screen. That is true of a STEP CHANGE. It is NOT true of `unmount`: React runs a parent's
     * cleanup AFTER its children have gone, so by the time this reads the store the duty point
     * cards have DEREGISTERED and `values.dutyPoints` is `[]`.
     *
     * ⚠️ SO THE LEAVE-THE-PAGE FLUSH WAS BLIND TO EVERYTHING ON TAB 2. MEASURED — a card with a
     * one-off command role on it reported `dutyPoints: []` at unmount, `armingOf` answered
     * `'no-content'`, and the work went with the page. That is the SECOND HALF of the "a custom
     * command role is not persisted" report: arming it was necessary and not sufficient.
     *
     * ⚠️ `(true)` IS ALSO THE ONLY READING CONSISTENT WITH `body()`, which has always used it.
     * A gate that tests a smaller value than the payload it guards can refuse to send a payload
     * that was perfectly valid, which is exactly what happened.
     *
     * ⚠️ AND IT DOES NOT WEAKEN [UX1.2]'s TWO NEGATIVES. An untouched card's values are a blank
     * place row, an empty `commandEntries` and seeded lines at zero — `dutyPointHasContent`
     * refuses all three by VALUE, never by presence — and `dirty` gates the whole thing first.
     * Both "leaving creates NO draft" tests are asserted unchanged.
     */
    const values = form.getFieldsValue(true) as Record<string, unknown> & { dutyPoints?: DutyPointFormValue[] };
    const arming = armingOf({
      readOnly,
      hydrated: hydrated.current,
      dirty: dirtyRef.current,
      hasContent: hasMeaningfulContent({
        pa: values,
        dutyPoints: values.dutyPoints ?? [],
        /*
         * ⚠️ [C3a §3] A chosen PDF is content, so attaching alone can create the draft — and
         * this reads the REF rather than the state, because `queueAttachment` flushes in the
         * same tick. See `pendingRef`'s header: the state form makes this line a no-op for the
         * first file, which is the whole of §3.
         */
        pendingAttachments: pendingRef.current.length,
      }),
      hasPaDate: Boolean(form.getFieldValue('paDate')),
      inFlight: inFlight.current,
    });

    /*
     * ─────────────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [AUTO-SAVE] WITH AUTOSAVE OFF, ONLY THE SAVE BUTTON GETS PAST THIS LINE.
     *
     *  Every other trigger stops here, BEFORE anything that claims a save was attempted: a blur on a
     *  form with no date does not turn the badge to "needs a date", because nothing tried to save.
     *
     *  ⚠️ BUT `no-content` STILL RESOLVES TO `idle`, AND THAT IS [C3a §7b]'s RULE KEPT, NOT A WRITE.
     *  `unsaved` is a claim about content; an operator who clears every field back to nothing must
     *  not be left with an enabled Save that does nothing and a prompt guarding nothing — which is
     *  what this would leave if the resolution waited for a press.
     * ─────────────────────────────────────────────────────────────────────────────────────
     */
    if (!triggerMaySave(trigger, autoSave)) {
      if (arming === 'no-content') markClean('idle');
      return;
    }

    if (arming === 'no-date') {
      writeSaveState('needs-date');
      return;
    }
    /* ⚠️ A TRIGGER THAT ARRIVES MID-WRITE QUEUES. Every body is the WHOLE record, so a
       dropped duplicate loses nothing and there is no ordering question to guard. */
    if (arming === 'in-flight') {
      queued.current = true;
      return;
    }
    /*
     * ─────────────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [C3a §7b] `no-content` RESOLVES TO `idle`, OR SAVE IS A CONTROL THAT DOES NOTHING —
     *  WHICH IS THE ONE THING §7b FORBIDS, AND IT WAS REACHABLE IN THE ORDINARY FLOW.
     *
     *  `markDirty` sets `unsaved` unconditionally, because `onValuesChange` cannot tell a
     *  STRUCTURAL change from typed content — `autosave.ts` records that. So `Add duty point`
     *  marks the form dirty while `newDutyPoint()` produces a blank place row, empty command
     *  entries and lines seeded at zero, every one of which `dutyPointHasContent` refuses BY
     *  VALUE. The badge then read "Not saved", Save rendered ENABLED, `publishControlState`
     *  withdrew Publish at the same moment — and pressing Save fell through this line with no
     *  request, no state change and no message.
     *
     *  ⚠️ IT IS HONEST RATHER THAN COSMETIC. `armingOf` checks `dirty` BEFORE `no-content`, so
     *  this arm means the form is dirty AND holds nothing worth keeping — which IS idle, and
     *  `SAVE_COPY.tooltip('idle')` already says "Nothing to save yet." The alternative was
     *  reading the whole form store on every keystroke inside `markDirty`, which is a cost this
     *  page pays once per blur today.
     *
     *  ⚠️ AND IT GOES THROUGH `markClean`, so `dirty` and `dirtyRef` move with `saveState` —
     *  the one-writer rule, which is also what stops `useUnsavedGuard` prompting over a form
     *  that holds nothing.
     * ─────────────────────────────────────────────────────────────────────────────────────
     */
    if (arming === 'no-content') {
      markClean('idle');
      return;
    }
    if (arming !== 'armed') return;

    inFlight.current = true;
    writeSaveState('saving');
    /* ⚠️ [C3a §7d] STAMPED HERE, beside `body()` — see `editSeq`. An edit that lands after this
       line is not covered by the write this line starts, whatever its success says. */
    sentSeq.current = editSeq.current;
    save.mutate(body());
  };

  /*
   * ⚠️ AN EFFECT, NOT A WRITE DURING RENDER. `eslint-plugin-react-hooks`'s `refs` rule refuses
   * the latter and is right to: a ref read during render cannot make the component update.
   * `useLayoutEffect` rather than `useEffect` so the ref is current before any child effect —
   * including the unmount cleanup below — could ever observe it.
   */
  useLayoutEffect(() => {
    flushRef.current = flush;
  });

  /**
   * ─────────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3a §3] THE DRAIN — one queued file at a time, once the record exists.
   *
   *  ⚠️ IT INVALIDATES ONLY `attachments(id)`, A STRICT DESCENDANT of `detail(id)`. Refetching a
   *  descendant cannot refetch its parent, so this cannot re-seed the form from the server's
   *  stripped copy — [P3b.3]'s defect, which `saveKeyIsolation.test.ts` exists to refuse.
   *
   *  ⚠️ AND A FAILED UPLOAD KEEPS ITS ROW. Dropping it would lose the file the operator chose
   *  with nothing on screen to say so; leaving it queued means the guard still fires on the way
   *  out and the row still reads "not saved yet".
   */
  useEffect(() => {
    /*
     * ⚠️ THE IN-FLIGHT GUARD IS LOAD-BEARING, AND WITHOUT IT A SECOND FILE UPLOADS THE FIRST
     * TWICE. This effect depends on `pendingAttachments`, so queueing OR dropping a file while
     * an upload is in flight changes the array's identity and re-runs it — and `queue[0]` is
     * still the same file, which is then POSTed a second time. Two attachment rows for one PDF,
     * and `ATTACHMENT_MAX_PER_ARRANGEMENT` reached in half the files.
     *
     * A ref rather than state, because it must be true for the NEXT run of this effect rather
     * than after a render — `inFlight`'s reason, one queue over.
     */
    if (savedId === undefined || pendingAttachments.length === 0 || drainingRef.current !== null) return;
    const next = pendingAttachments[0];
    if (!next) return;
    drainingRef.current = next.key;

    const form = new FormData();
    form.append('file', next.file);
    void api
      .post<AttachmentDto>(`/arrangements/${savedId}/attachments`, form)
      .then(() => {
        drainingRef.current = null;
        writePending(pendingRef.current.filter((item) => item.key !== next.key));
        void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.attachments(savedId) });
      })
      .catch((error: ApiError) => {
        drainingRef.current = null;
        void message.error(error.message);
        /* ⚠️ Dropped from the QUEUE but not from the operator's attention — the toast is the
           report, and re-queuing it would retry for ever against a file the server refused. */
        writePending(pendingRef.current.filter((item) => item.key !== next.key));
      });
  }, [savedId, pendingAttachments, queryClient, message]);

  /**
   * ⚠️ [C1f §6] THE THIRD TRIGGER — ON THE WAY OUT.
   *
   * Mounted once and reading `flushRef`, because a cleanup closing over the first render
   * would flush a form that was empty at the time. `armingOf` decides whether it writes, so
   * opening `/arrangements/new` and leaving still issues NOTHING: `dirty` is false, and the
   * page has never issued a POST or a GET on mount either.
   */
  useEffect(
    () => () => {
      flushRef.current('unmount');
    },
    [],
  );

  /**
   * ⚠️ [amended C1f §6] THE GUARD SPLITS, BECAUSE AN IN-APP EXIT CAN BE FOLLOWED BY A WRITE
   * AND AN UNLOAD CANNOT.
   *
   * `useUnsavedGuard`'s click path calls `window.location.assign`, a full document load that
   * runs no React cleanup — so leaving `when` on `dirty` would mean the GUARDED exit skips
   * the unmount flush while the ordinary one performs it. `when` is therefore only what
   * autosave CANNOT save; `whenUnload` stays the broader "the server has not acknowledged
   * this yet", because `beforeunload` is the one event a request cannot outlive.
   */
  /*
   * ⚠️ [AUTO-SAVE] WITH AUTOSAVE OFF NOTHING IS SAVED ON THE WAY OUT, so every exit that would lose
   * work asks first — both arms read the one fact `publishControl.ts` reads, `saveIsPending`, plus
   * the queue. `saving` is in it deliberately: the guard's own exit is a full page load, which a
   * request still in flight may not outlive.
   */
  const unsavedWork = saveIsPending(saveState) || pendingAttachments.length > 0;
  useUnsavedGuard({
    /*
     * ⚠️ [amended C3a §3] THE QUEUE JOINS BOTH ARMS, AND THAT IS WHAT MAKES HOLDING THE FILE
     * SAFE. A queued PDF is work the server has never seen: leaving with one raises the prompt
     * rather than dropping it silently, which is the whole objection to holding rather than
     * forcing the draft into existence.
     */
    when: autoSave
      ? saveState === 'failed' || saveState === 'needs-date' || pendingAttachments.length > 0
      : unsavedWork,
    whenUnload: autoSave ? dirty || save.isPending || pendingAttachments.length > 0 : unsavedWork,
    confirm: () =>
      new Promise<boolean>((resolve) => {
        modal.confirm({
          /* ⚠️ [AUTO-SAVE] One sentence per reason — `guardReasonOf`; a queued file is "unsaved" now. */
          title: GUARD_TITLE[guardReasonOf(saveState)],
          okText: 'Leave',
          cancelText: 'Stay',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
  });

  /**
   * ⚠️ SEEDING AN EXISTING ARRANGEMENT. `initialValues` is applied ONCE, at mount, and the
   * record has not arrived yet — so it is `setFieldsValue` in an effect, as `PolicyBuilder`
   * does.
   *
   * ⚠️ AND `hydrated` GATES AUTOSAVE, WHICH IS THE WHOLE BUG. This effect used to seed the
   * TITLE ALONE, so opening a saved order showed an empty form with the right name — and
   * because autosave fires from the first keystroke, the next character PATCHed that empty
   * form over the record and took the places, the schedule, the grid and the roster with
   * it. Seeding everything is half the fix; the other half is that `setFieldsValue` itself
   * must not look like a user edit, or the seeding write is the thing that clears the
   * record.
   */
  useEffect(() => {
    if (!existing.data) return;
    /*
     * ⚠️ ONCE PER RECORD, AND THE GUARD IS THE WHOLE FIX FOR A DATA-LOSS BUG.
     *
     * This effect had only `if (!existing.data) return`, so it re-seeded on every new
     * IDENTITY of `existing.data` — and a save invalidates `queryKeys.arrangements.all`,
     * which is a PREFIX of `detail(id)`, so every save refetched this record and overwrote
     * the form with the server's copy.
     *
     * That copy is not what is on screen: `body()` sends `submittableLines`, which strips a
     * cell at zero and a source with no division chosen. So the reported symptoms were all
     * one bug — a `10` typed down to `1` snapped back to whatever the last completed write
     * had stored, a vehicle `0` typed to `1` snapped back to `0`, and a freshly added source
     * row (all zeros, no division yet) simply vanished a moment after appearing.
     *
     * The record is seeded from the server exactly once; from then on the form is the truth
     * until the operator saves it.
     */
    if (seededFor.current === existing.data.id) return;
    seededFor.current = existing.data.id;
    form.setFieldsValue(fromArrangement(existing.data));
    /*
     * ⚠️ AFTER THE SEEDING WRITE, NEVER WITH THE DATA THAT CAUSED IT. `setFieldsValue`
     * fires `onValuesChange`, so a flag derived from `existing.data` would already be true
     * when this line runs and the seeding write would autosave an empty form over the
     * record — which is the P3a.1 defect this flag exists to prevent.
     *
     * (`setSavedId(existing.data.id)` used to sit here and was a provable no-op: this query
     * is enabled only for `arrangementId`, and `savedId` is initialised from it.)
     */
    hydrated.current = true;
  }, [existing.data, form]);

  /**
   * Names for the `SPECIFIC` subset picker. Every officer named on any role, resolved once
   * — the picker must never render a bare ObjectId beside a checkbox.
   */
  const commandOfficers = useMemo(() => {
    const byId = new Map<string, { name: string }>();
    /* ⚠️ [amended D4] Across every card — an officer named on card 3 must resolve too. */
    for (const point of existing.data?.dutyPoints ?? []) {
      for (const entry of point.commandEntries) {
        for (const officer of entry.officers) byId.set(officer.id, { name: officer.name });
      }
    }
    return byId;
  }, [existing.data]);

  /**
   * ⚠️ [C6a §2] THE RECORD'S ASKED COMMAND POSITIONS, PER CARD AND THEN BY ASK ID — resolved once
   * from the SAVED record. On an issued order each carries the seat its addressee answered with;
   * that answer is read-only here and is never written into the form, whose next autosave would
   * otherwise send a division's choice back as the creator's.
   *
   * ⚠️ KEYED ON THE CARD FIRST, NEVER ON THE ASK ID ALONE — an ask id is unique on its duty point
   * only; `commandAsksByDutyPoint` states why. Each card is handed its OWN map (`DutyPointStack`).
   */
  const commandAsks = useMemo(() => commandAsksByDutyPoint(existing.data?.dutyPoints ?? []), [existing.data]);

  const blockers = preview.data?.blockers ?? [];
  /*
   * ⚠️ WARNINGS DO NOT DISABLE PUBLISH, and that is a decision about the roll-out. On day
   * one the officer master is incomplete, so an order with a required command role that
   * nobody can be named to is the normal case. Refusing it would mean no arrangement could
   * be raised until the establishment was fully mapped. The server draws the same line —
   * see `publishPreviewSchema.warnings`.
   */
  const warnings = preview.data?.warnings ?? [];

  /**
   * ⚠️ [C1f §7] THE ONE CONTROL'S MEANING, DERIVED IN `publishControl.ts` AND NOWHERE ELSE.
   *
   * ⚠️ `publishedRecord` IS POSITIVE, NEVER `!isDraft`. `isDraft` is deliberately
   * hide-then-show for the destructive `Discard` — an unresolved record answers "not a draft"
   * so the button cannot flicker in a moment later — and read backwards it would put
   * `Assign names` on a DRAFT that is still loading.
   */
  const publishedRecord = existing.data !== undefined && existing.data.status !== 'DRAFT';
  const control = publishControlState({
    publishedRecord,
    canPublish: canPublish && !readOnly,
    savedId,
    blockerCount: blockers.length,
    /* ⚠️ [C3a §7d] The SAME value `markDirty`/`markClean` write. Never a second `dirty` flag. */
    saveState,
    autoSave,
  });
  const controlTitle =
    control === 'published'
      ? PUBLISH_COPY.assignNamesTooltip
      : control === 'refused'
        ? READ_ONLY_TOOLTIP
        : control === 'save'
          ? SAVE_COPY.tooltip(saveState, publishedRecord)
          : control === 'blocked'
            ? PUBLISH_COPY.blockedTooltip
            : PUBLISH_COPY.publish;

  /*
   * ⚠️ THE CHECKLIST JUMPS ACROSS STEPS, and that is the whole reason the steps are not a
   * wizard. Clicking "Nobody has been asked for" from the right rail must land on the force
   * grid whether or not that step happens to be the one showing.
   */
  const jump = (id: string): void => {
    const target = stepOfSection(ARRANGEMENT_SECTIONS, FORM_STEPS, id);
    if (target >= 0) setStep(target);
    const node = sectionRefs.current.get(id);
    /*
     * ⚠️ THE OPTIONAL CALL IS REQUIRED, NOT DEFENSIVE STYLE. jsdom implements no layout and
     * has NO `Element#scrollIntoView` — `HierarchyTable.tsx` already says so and already
     * writes `?.()`. Without it this line THREW on every checklist jump under test, vitest
     * reported an unhandled error and exited 1, and the `focus()` below — the accessibility
     * requirement, not the nicety — had never once run.
     */
    node?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    // Focus follows the scroll: an accessibility requirement, not a nicety — a keyboard
    // user who clicks the checklist must land IN the section, not at the top of the page.
    node?.querySelector<HTMLElement>('input, button, [tabindex]')?.focus();
  };

  /**
   * Focus follows an append, and that is what keeps the header buttons from being a tab-order
   * regression.
   *
   * With Add AFTER the rows, a clerk entering five places never reverses direction. In the
   * header it sits BEFORE them, so reaching it costs a Shift-Tab past every row and every
   * remove button — nine reverse stops at five places, and growing. Landing the operator
   * inside the new row makes that distance irrelevant.
   *
   * ⚠️ rAF, because the row does not exist until the next render.
   */
  const focusLastIn = useCallback((sectionId: string, selector: string): void => {
    requestAnimationFrame(() => {
      const nodes = sectionRefs.current.get(sectionId)?.querySelectorAll<HTMLElement>(selector);
      nodes?.[nodes.length - 1]?.focus();
    });
  }, []);

  const registerSection = useCallback((id: string, node: HTMLElement): void => {
    sectionRefs.current.set(id, node);
  }, []);

  /**
   * ⚠️ THE APPEND BASE IS THE FORM STORE, NEVER `draft`. `toDraft` filters blank names out
   * of `locationNames`, so appending to the draft would drop the empty row the operator is
   * typing into — divergence-register #30, again.
   *
   * ⚠️ AND `onValuesChange()` IS MANDATORY, AND SILENT IF FORGOTTEN. rc-field-form fires it
   * from `updateValue` — the path a `Form.List`'s own `add()` takes — but NOT from
   * `setFieldValue`. Miss it and adding a place stops marking the form dirty and stops
   * scheduling the autosave, with a green suite, because nothing asserts autosave-on-add.
   * `onAddAdHoc` and the grid's `onChange` already call it by hand for the same reason.
   */
  /** The shared columns as the FORM STORE holds them — never the draft, which drops the stash. */
  const storedShared = (): SharedColumnInputDto[] =>
    (form.getFieldValue('sharedColumns') ?? []) as SharedColumnInputDto[];

  /**
   * ⚠️ [C5c §2] THE ONE WRITER OF BOTH FIELDS, AND EVERYTHING GOES THROUGH `normalizeSharing`.
   *
   * A card write can break a share in ways no single action can see — a duplicated anchor copies
   * the column onto a covered card, a removal leaves one card, a re-seed puts the column back — so
   * the net runs on every write rather than each action remembering its own case. Both fields in
   * ONE `setFieldsValue`, so no render ever sees cards and shares that disagree.
   */
  const writeSharing = (state: SharingState<DutyPointFormValue>): void => {
    const next = normalizeSharing({ ...state, zeroLineFor });
    form.setFieldsValue({ dutyPoints: next.cards, sharedColumns: next.shared });
    onValuesChange();
  };

  /** Read the cards out of the FORM STORE, mutate, write back, and mark the form dirty. */
  const editCards = (mutate: (cards: DutyPointFormValue[]) => DutyPointFormValue[]): void => {
    const cards = (form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[];
    writeSharing({ cards: mutate([...cards]), shared: storedShared() });
  };

  const appendPlace = (index: number): void => {
    editCards((cards) =>
      cards.map((card, at) =>
        at === index ? { ...card, places: [...card.places, { placeId: null, name: '' }] } : card,
      ),
    );
    focusLastIn('dutyPoints', '[data-testid^="location-"] input');
  };

  const appendShift = (index: number): void => {
    editCards((cards) =>
      cards.map((card, at) =>
        at === index ? { ...card, shifts: [...card.shifts, { id: mintShiftId(), startTime: '', endTime: '' }] } : card,
      ),
    );
  };

  const addDutyPoint = (): void => {
    const paDate = form.getFieldValue('paDate') as DateTime | undefined;
    /* ⚠️ [amended C1f §4] THE ONE PLACE A NEW CARD LEARNS ITS POLICY. See `policyDefault.ts`. */
    const fresh = newDutyPoint(paDate, defaultPolicyFor(activePolicies));
    /*
     * ⚠️ [C5c §2c] WHILE A COLUMN IS SHARED, A NEW CARD TAKES THE SHARED BINDING — every card binds
     * one version (2f) — so it shows the dash from its first render and the seed leaves the shared
     * column off it. The anchor's values exactly as stored, `undefined` included: never a pin.
     */
    const cards = (form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[];
    const anchor = cards.find((row) => row.id === storedShared()[0]?.anchorDutyPointId);
    const card: DutyPointFormValue = anchor
      ? {
          ...fresh,
          policyId: anchor.policyId,
          policyVersionId: anchor.policyVersionId,
          columnsFromVersionId: anchor.columnsFromVersionId,
        }
      : fresh;
    editCards((rows) => [...rows, card]);
    /* ⚠️ OPENS THE NEW CARD AND COLLAPSES THE REST — what makes expanding cost no clicks. */
    setOpenCards([card.id]);
  };

  /**
   * ⚠️ FOUR DECISIONS THE BRIEF IS SILENT ON, AND EACH ONE COSTS OR SAVES CLICKS.
   *
   *  1. INSERTED BELOW ITS SOURCE, not appended — a copy belongs beside the thing copied.
   *  2. SHIFT IDS ARE RE-MINTED. `slotKey` is `<dutyDate>#<shiftId>`, so two cards sharing
   *     a shift id would have their per-slot overrides collide (divergence #21, one level
   *     down).
   *  3. `perSlot` IS DROPPED, the role-level officer list KEPT. An invisible override coming
   *     back into force is exactly what `pruneHiddenState` exists to prevent; the officers
   *     are the click-saver.
   *  4. THE PLACE IS CLEARED. Two duty points at one place on one schedule are ONE duty
   *     point — copying it would ship a plausible twin, and the operator's next act is to
   *     type the new place anyway.
   *  5. [D4.3] THE INSTRUCTIONS COME WITH IT, by the spread and deliberately. `perSlot` and
   *     the slot overrides are dropped because anything keyed on a date is orphaned when the
   *     schedule re-anchors; a standing order for a place names no date at all, and re-typing
   *     it is exactly the work duplicating an order exists to save. A one-off COMMAND role
   *     travels for the same reason its officers do — it is the card's own vocabulary.
   */
  const duplicateDutyPoint = (index: number): void => {
    const cards = (form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[];
    const source = cards[index];
    if (!source) return;
    const copy: DutyPointFormValue = {
      ...source,
      id: mintDutyPointId(),
      places: [{ placeId: null, name: '' }],
      shifts: source.shifts.map((shift) => ({ ...shift, id: mintShiftId() })),
      /* ⚠️ [C6a] A deep copy with every asked position RE-MINTED — see `duplicateCommandEntries`. */
      commandEntries: duplicateCommandEntries(source.commandEntries),
      requirementLines: source.requirementLines.map((line) => ({
        ...line,
        allocations: line.allocations.map((allocation) => ({ ...allocation })),
      })),
    };
    editCards((rows) => [...rows.slice(0, index + 1), copy, ...rows.slice(index + 1)]);
    setOpenCards([copy.id]);
    notifyDevChange({
      where: 'Assign duty',
      what: 'duty point duplicated — times, policy and the whole force grid copied',
      why: 'Most bandobast is several near-identical points, so duplicating and changing the place beats every other affordance on this screen. The place is cleared because two points at one place on one schedule are one point.',
    });
  };

  /**
   * ⚠️ [C5c §2g] REMOVING AN ANCHOR ENDS SHARING FOR ITS COLUMNS AND GIVES EVERY OTHER CARD ITS
   * OWN NUMBER BACK — asked first, because the operator may not know the card was the anchor. It is
   * never re-homed onto the next card: "the next card" is a position.
   */
  const removeDutyPoint = (index: number): void => {
    const cards = (form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[];
    const card = cards[index];
    const shared = storedShared();
    const anchored = card ? anchoredKeys(shared, card.id) : new Set<string>();
    if (!card || anchored.size === 0) {
      editCards((rows) => rows.filter((_card, at) => at !== index));
      return;
    }
    const view = dutyPoints.find((row) => row.id === card.id);
    const names = [...anchored].map((key) => {
      const lineIndex = card.requirementLines.findIndex((line) => lineKeyOf(line) === key);
      return view?.grid.columns.find((column) => column.lineIndex === lineIndex)?.fullLabel ?? key;
    });
    modal.confirm({
      title: SHARE_COPY.removeAnchorTitle,
      content: SHARE_COPY.removeAnchorBody(names),
      okText: SHARE_COPY.removeAnchorOk,
      okButtonProps: { danger: true },
      onOk: () => {
        const latest = (form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[];
        writeSharing(removeCard({ cards: latest, shared: storedShared(), cardId: card.id, zeroLineFor }));
      },
    });
  };

  /** ⚠️ BY ID, NEVER BY INDEX — `Form.Item` names ARE indices. See `DutyPointStack`. */
  const reorderDutyPoints = (ids: string[]): void => {
    editCards((cards) => ids.flatMap((id) => cards.filter((card) => card.id === id)));
  };

  const setRequirementLines = (index: number, lines: RequirementLineInputDto[]): void => {
    editCards((cards) => cards.map((card, at) => (at === index ? { ...card, requirementLines: lines } : card)));
  };

  /**
   * FR-5.2.6c — a ONE-OFF column, on THIS duty point only, that the policy does not carry.
   *
   * ⚠️ APPENDED AFTER THE POLICY'S OWN COLUMNS, AND CARRYING NO ID. That is exactly why a
   * grid column's identity is its line's INDEX rather than an ObjectId (`lib/forceGrid.ts`),
   * and why `requirementColumns` needs no new arm: a one-off row has nothing to key on but
   * its label, which `lineKeyOf` slugs to `A:<slug>`.
   *
   * ⚠️ IT NEVER WRITES BACK TO THE POLICY. Creating a genuinely new post type stays an admin
   * action; this row lives on the arrangement and dies with it.
   *
   * ⚠️ AND THE BASE IS THE FORM STORE, VIA `editCards`, NEVER `draft` — the same rule
   * `appendPlace` states above, plus the `onValuesChange()` that `editCards` performs for us.
   */
  /**
   * ⚠️ [D4.3] REMOVES BY POSITION, WHICH IS THE COLUMN'S IDENTITY AND NOT A SHORTCUT.
   *
   * `lib/forceGrid.ts` keys a grid column on its line's INDEX precisely because a one-off row
   * has no id to key on. Splicing the array therefore re-points every column to its right by
   * one — which is correct, because the grid is re-derived from the same array in the same
   * render pass, and it is why the caller may only ever pass an index it just read from that
   * grid. The rule the CHANGELOG cares about is one level up: a published order's ASSIGNMENTS
   * key on `A:<slug>` (`fillRef.util.ts`), never on a position, so a removal here cancels
   * exactly the slots for this label and re-points nothing else.
   */
  const removeAdHocColumn = (index: number, lineIndex: number): void => {
    const cards = (form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[];
    const card = cards[index];
    const line = card?.requirementLines[lineIndex];
    /*
     * ⚠️ [C5c §2e] A SHARED ONE-OFF COLUMN STOPS BEING SHARED FIRST, SO EVERY OTHER CARD GETS ITS
     * OWN NUMBER BACK. A one-off column's label IS its identity, so once the anchor's line has gone
     * nothing can say what the stash was a number FOR — `normalizeSharing` would have to drop it.
     */
    const state =
      card && line && anchoredKeys(storedShared(), card.id).has(lineKeyOf(line))
        ? disableSharing({ cards, shared: storedShared(), lineKey: lineKeyOf(line), zeroLineFor })
        : { cards, shared: storedShared() };
    writeSharing({
      shared: state.shared,
      cards: state.cards.map((row, at) =>
        at === index
          ? { ...row, requirementLines: row.requirementLines.filter((_line, at2) => at2 !== lineIndex) }
          : row,
      ),
    });
  };

  const addAdHocColumn = (index: number, label: string): void => {
    /*
     * ⚠️ [C5c §2] THE MODAL REFUSES A ONE-OFF COLUMN THIS CARD IS ALREADY COVERED FOR; this is the
     * backstop, because `normalizeSharing` would otherwise remove the line silently a moment after
     * it was added.
     */
    const target = ((form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[])[index];
    const key = lineKeyOf({ postTypeId: null, vehicleTypeId: null, adHocLabel: label });
    if (target && coveringKeys(storedShared(), target.id).has(key)) return;
    editCards((cards) =>
      cards.map((card, at) =>
        at === index
          ? {
              ...card,
              requirementLines: [
                ...card.requirementLines,
                {
                  postTypeId: null,
                  vehicleTypeId: null,
                  adHocLabel: label,
                  /* ⚠️ [C1c §4a] EMPTY IS ANY — a new one-off column behaves exactly as it did. */
                  restrictToPostTypeIds: [],
                  requiredCount: 0,
                  allocations: [],
                },
              ],
            }
          : card,
      ),
    );
  };

  /**
   * ⚠️ [C1c §4b] WHICH POSTS MAY FILL ONE ONE-OFF COLUMN, ON ONE CARD.
   *
   * It writes only the restriction: `requiredCount` and `allocations` are what the operator has
   * typed into the grid, and rebuilding the line would reset them — `flattenRequirementLines`'
   * rule one layer up, and the reason `useCurrentColumns` above carries numbers over by id.
   */
  const restrictAdHocColumn = (index: number, lineIndex: number, postTypeIds: readonly string[]): void => {
    editCards((cards) =>
      cards.map((card, at) =>
        at === index
          ? {
              ...card,
              requirementLines: card.requirementLines.map((line, at2) =>
                at2 === lineIndex ? { ...line, restrictToPostTypeIds: [...postTypeIds] } : line,
              ),
            }
          : card,
      ),
    );
  };

  /**
   * ⚠️ CHANGING A CARD'S POLICY CLEARS ITS GRID, and the alternative is worse.
   *
   * The columns ARE the policy, so keeping the old lines would leave a card asking for
   * columns its new version does not carry — which §5c would then read as a FORK the
   * operator never asked for. Clearing lets the seeding effect fill in the new columns.
   */
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C1a §2] TAKE THE CURRENT VERSION'S COLUMNS, KEEPING EVERY NUMBER THAT SURVIVES.
   *
   *  The offered half of the moved notice. An ACTION rather than an effect precisely because
   *  this grid has been typed into — `PolicyBuilder`'s retired-column offer is the
   *  precedent: "an OFFER, not an automatic removal… does nothing until it is pressed".
   *
   *  ⚠️ IT GOES THROUGH `editCards`, so `onValuesChange()` fires and the form goes DIRTY.
   *  This IS an operator edit; `setFieldValue` here would leave the page claiming it had
   *  nothing to save.
   *
   *  ⚠️ AND IT STILL WRITES NO `policyVersionId`. Same rule as the seed: the server
   *  resolves, and [PRE-P6] §3a's deletion stands.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const useCurrentColumns = (index: number): void => {
    const cards = (form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[];
    const target = cards[index];
    const targetVersion = target ? versionOf(target) : undefined;
    if (!target || !targetVersion) return;
    const shared = storedShared();
    /*
     * ⚠️ [C5c §2] WHILE A COLUMN IS SHARED EVERY CARD BINDS THIS ONE VERSION (2f), so updating one
     * card's columns is updating every card's — leaving the others on the old columns would break
     * the rule the server asserts on the next save.
     */
    const applies = (card: DutyPointFormValue, at: number): boolean =>
      shared.length > 0 ? Boolean(card?.id) : at === index;
    const offeredKeys = new Set(seededLines(resolvePolicyColumns(targetVersion)).map(lineKeyOf));
    /*
     * ⚠️ [C5c §2e] A SHARED POLICY COLUMN THE NEW VERSION DROPS takes every other card's set-aside
     * number with it — the column is not on the version they are all moving to. Asked first, with
     * the numbers; a one-off column is the order's own and survives any version.
     */
    const lost = shared.filter((entry) => !entry.lineKey.startsWith('A:') && !offeredKeys.has(entry.lineKey));
    const keptShared = shared.filter((entry) => !lost.includes(entry));

    const apply = (): void => {
      writeSharing({
        shared: keptShared,
        cards: cards.map((card, at) => {
          if (!applies(card, at)) return card;
          const version = versionOf(card);
          if (!version) return card;
          const columns = resolvePolicyColumns(version);
          const kept = new Map(
            card.requirementLines
              .filter((line) => !line.adHocLabel)
              .map((line) => [line.postTypeId ?? line.vehicleTypeId ?? '', line]),
          );
          return {
            ...card,
            requirementLines: [
              // The new version's own order, carrying over what the operator already typed.
              ...seededLines(columns, coveringKeys(keptShared, card.id)).map((line) => {
                const previous = kept.get(line.postTypeId ?? line.vehicleTypeId ?? '');
                return previous
                  ? { ...line, requiredCount: previous.requiredCount, allocations: previous.allocations }
                  : line;
              }),
              // ⚠️ Ad-hoc lines are the ORDER's, never the policy's (FR-5.2.6c), so they
              // survive untouched — at the end, where `addAdHocColumn` puts them.
              ...card.requirementLines.filter((line) => line.adHocLabel),
            ],
            columnsFromVersionId: version.id,
          };
        }),
      });
    };

    if (lost[0] === undefined) {
      apply();
      return;
    }
    const view = dutyPoints.find((row) => row.id === target.id);
    modal.confirm({
      title: SHARE_COPY.versionDropsTitle,
      content: SHARE_COPY.versionDropsBody(
        lost.map((entry) => {
          const lineIndex = target.requirementLines.findIndex((line) => lineKeyOf(line) === entry.lineKey);
          const column =
            view?.grid.columns.find((row) => row.lineIndex === lineIndex)?.fullLabel ??
            view?.coveredColumns.find((row) => row.lineKey === entry.lineKey)?.fullLabel ??
            entry.lineKey;
          return { column, setAside: entry.stashed.reduce((sum, row) => sum + row.requiredCount, 0) };
        }),
      ),
      okText: SHARE_COPY.versionDropsOk,
      onOk: apply,
    });
  };

  const setPolicy = (index: number, policyId: string): void => {
    /* ⚠️ [C5c §2f] The picker is disabled while a column is shared; this is the backstop. */
    if (storedShared().length > 0) return;
    editCards((cards) =>
      cards.map((card, at) =>
        at === index
          ? {
              ...card,
              policyId,
              policyVersionId: undefined,
              /* ⚠️ [C1a §2] The provenance goes with the lines it described, or the empty
                 grid would be re-seeded and then instantly reported as "the policy moved". */
              columnsFromVersionId: undefined,
              requirementLines: [],
            }
          : card,
      ),
    );
  };

  const toggleCard = (id: string): void => {
    setOpenCards((open) => (open.includes(id) ? open.filter((row) => row !== id) : [...open, id]));
  };

  /** `DP2`, the ordinal every reader of the order prints, for a card id. */
  const cardLabel = (id: string): string => {
    const view = dutyPoints.find((row) => row.id === id);
    return view ? `DP${String(view.index + 1)}` : 'A duty point';
  };

  /** A column's full name from any version the page has loaded — the dropped columns are the card's own. */
  const columnNameOf = (lineKey: string): string => {
    const id = lineKey.slice(2);
    for (const version of versionById.values()) {
      const columns = resolvePolicyColumns(version);
      const hit = lineKey.startsWith('P:')
        ? columns.allPostTypes.get(id)
        : lineKey.startsWith('V:')
          ? columns.allVehicleTypes.get(id)
          : undefined;
      if (hit) return hit.name;
    }
    return 'a column';
  };

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  [C5c §2] SHARE ONE CARD'S COLUMN ACROSS EVERY CARD, OR STOP SHARING IT.
   *
   *  A dry run first, then the confirmation states what it does — each other card's number set
   *  aside, each card moved onto the anchor's policy and the columns it loses, with their counts —
   *  and the write goes through `writeSharing`, recomputed from the store at the click.
   *
   *  ⚠️ §2f IS A REFUSAL, NOT A WARNING. Moving a card onto another version can drop NAMED
   *  supervisors: a seat whose role that version lacks is still sent while nothing renders it, and
   *  an officer whose rank its roster refuses makes the next save 400. So the count is stated and
   *  nothing is written; the operator clears the seats or aligns the policies first.
   *
   *  ⚠️ THE RANK MASTER IS FETCHED HERE, ONLY WHEN A CARD WOULD MOVE — the same key and function
   *  `useRankOptions` uses, so it is one cache entry, and no builder mount pays a request for a
   *  question it may never ask.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const toggleShare = (index: number, lineIndex: number): void => {
    const cards = (form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[];
    const card = cards[index];
    const line = card?.requirementLines[lineIndex];
    if (!card || !line) return;
    const lineKey = lineKeyOf(line);
    const shared = storedShared();
    const view = dutyPoints.find((row) => row.id === card.id);
    const column = view?.grid.columns.find((row) => row.lineIndex === lineIndex)?.fullLabel ?? columnNameOf(lineKey);
    const anchorLabel = cardLabel(card.id);

    if (anchoredKeys(shared, card.id).has(lineKey)) {
      modal.confirm({
        title: SHARE_COPY.disableTitle(column),
        content: SHARE_COPY.disableBody(shared.find((entry) => entry.lineKey === lineKey)?.stashed.length ?? 0),
        okText: SHARE_COPY.disableOk,
        onOk: () => {
          const latest = (form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[];
          writeSharing(disableSharing({ cards: latest, shared: storedShared(), lineKey, zeroLineFor }));
        },
      });
      return;
    }

    const anchorVersion = versionOf(card);
    if (!anchorVersion) return;
    const anchorColumns = seededLines(
      resolvePolicyColumns(anchorVersion),
      new Set(shared.map((entry) => entry.lineKey)),
    );
    const moves = cards.some(
      (row) =>
        Boolean(row?.id) &&
        row.id !== card.id &&
        (row.policyId !== card.policyId || row.policyVersionId !== card.policyVersionId),
    );

    void (async () => {
      let ranks: readonly RankDto[] = [];
      if (moves) {
        try {
          ranks = (
            await queryClient.fetchQuery({
              queryKey: queryKeys.ranks.options(),
              queryFn: () => api.get<Paginated<RankDto>>('/ranks', { query: { limit: PAGINATION.MAX_LIMIT } }),
              staleTime: 5 * 60_000,
            })
          ).items;
        } catch {
          /* Without the master no seat can be judged safe — refuse rather than guess. */
          modal.error({ title: SHARE_COPY.seatsTitle, content: SHARE_COPY.seatsUnchecked });
          return;
        }
      }
      const rankIdByCode = new Map(ranks.flatMap((rank) => (rank.code ? [[rank.code, rank.id] as const] : [])));
      const rankIdByShortName = new Map(ranks.map((rank) => [rank.shortName, rank.id] as const));
      const rankOfOfficer = new Map<string, string>();
      for (const point of existing.data?.dutyPoints ?? []) {
        for (const entry of point.commandEntries) {
          for (const officer of entry.officers) {
            const rankId = officer.rankShortName ? rankIdByShortName.get(officer.rankShortName) : undefined;
            if (rankId) rankOfOfficer.set(officer.id, rankId);
          }
        }
      }

      const run = (latest: DutyPointFormValue[]) =>
        enableSharing({
          cards: latest,
          shared: storedShared(),
          anchorId: card.id,
          lineKey,
          anchorColumns,
          nameOf: columnNameOf,
          seatsLost: (row) =>
            seatsLostOnConverge({
              entries: row.commandEntries,
              fromRoles: versionOf(row)?.commandRoles ?? [],
              toRoles: anchorVersion.commandRoles,
              rankOf: (officerId) => rankOfOfficer.get(officerId),
              rankIdByCode,
            }),
          /* ⚠️ [C6a §2] A role ASKED of a division that the anchor's version drops — stated, by label. */
          asksLost: (row) =>
            asksLostOnConverge({
              entries: row.commandEntries,
              fromRoles: versionOf(row)?.commandRoles ?? [],
              toRoles: anchorVersion.commandRoles,
            }),
        });

      const dry = run(cards);
      if (!dry.ok) {
        if (dry.lostSeats[0] === undefined) return;
        modal.error({
          title: SHARE_COPY.seatsTitle,
          content: (
            <span data-testid="share-refused">
              {SHARE_COPY.seatsBody(
                dry.lostSeats.map((row) => ({ card: cardLabel(row.cardId), count: row.count, asked: row.asked })),
              )}
            </span>
          ),
        });
        return;
      }

      const policyName = resolvePolicy(card.policyId ?? '')?.name ?? 'the same policy';
      const lines = [
        SHARE_COPY.enableIntro(column, anchorLabel),
        ...dry.report.converged.flatMap((row) => [
          SHARE_COPY.converged(cardLabel(row.cardId), policyName),
          ...(row.dropped[0] === undefined ? [] : [SHARE_COPY.dropped(cardLabel(row.cardId), row.dropped)]),
        ]),
        ...dry.report.setAside
          .filter((row) => row.requiredCount > 0)
          .map((row) => SHARE_COPY.setAside(cardLabel(row.cardId), row.requiredCount)),
        ...(dry.report.converged.some((row) => row.dropped[0] !== undefined) ? [] : [SHARE_COPY.nothingLost]),
      ];
      modal.confirm({
        title: SHARE_COPY.enableTitle(column, anchorLabel),
        content: (
          <div className="flex flex-col gap-1" data-testid="share-confirm">
            {lines.map((sentence) => (
              <p key={sentence}>{sentence}</p>
            ))}
          </div>
        ),
        okText: SHARE_COPY.enableOk,
        onOk: () => {
          const next = run((form.getFieldValue('dutyPoints') ?? []) as DutyPointFormValue[]);
          if (next.ok) writeSharing({ cards: next.cards, shared: next.shared });
        },
      });
    })();
  };

  /**
   * Everything the registry's descriptors may read, assembled once per render.
   *
   * ⚠️ THE IMPERATIVE HALF SITS UNDER `actions`, ON PURPOSE. It keeps `visibleWhen` and
   * `validate` pure functions of data — which is what lets them be unit-tested without a
   * form instance, and what stops a descriptor quietly acquiring a side effect.
   */
  const sectionState: ArrangementSectionState = {
    draft,
    dutyPoints,
    policies: policyOptions,
    resolvePolicy,
    officers: commandOfficers,
    orgUnits,
    commandAsks,
    /* ⚠️ [C6a review] `true` before there is a record — a draft asks nothing yet, so nothing is awaited. */
    awaitedLive: existing.data ? awaitedIsLive(existing.data) : true,
    derivedTitle,
    readOnly,
    /*
     * ⚠️ [C3c §9a] THE ORDER HAS STARTED. `firstDutyDate` is `null` on a draft with no schedule,
     * which reads as NOT started and leaves the form open — the fail-open direction is correct
     * here, because an absent start date means there is none to have passed.
     *
     * ⚠️ IT READS THE BROWSER'S CLOCK, which is why this is an OFFER gate and not a control —
     * see `BasicsSection`'s own header. `updateArrangement` refuses none of these fields.
     */
    /*
     * ⚠️⚠️ [amended C3c.2] A **DRAFT** IS NEVER IDENTITY-LOCKED, AND THE CLOCK TERM ALONE WAS THE BUG.
     *
     * This read the duty date and nothing else, so an order RAISED TODAY FOR TODAY — which is the
     * ordinary way an urgent bandobast is entered — opened with "This order has started" and its
     * header frozen, before it had been published at all. Nothing had started: there was no
     * published order, no duty, and no officer who could have been told anything.
     *
     * ⚠️ IT MIRRORS `dutyPointsLocked` BELOW, WHICH ALREADY CARRIED THE STATUS TERM. That one was
     * corrected at [C3c.1] to fire on `status !== 'DRAFT'`; this one was left on the clock, so the
     * two locks on one screen disagreed about what "started" means. They now ask the same question
     * first — is this still a draft? — and only then differ on the second term.
     *
     * ⚠️ AND THE CLOCK TERM STAYS FOR A NON-DRAFT, which is the half that was right: a PUBLISHED
     * order whose duty has begun is circulating on paper, and its date and crowd estimate are what
     * that paper says.
     */
    identityLocked:
      existing.data !== undefined &&
      existing.data.status !== 'DRAFT' &&
      existing.data.firstDutyDate != null &&
      existing.data.firstDutyDate <= istDutyDate(new Date()),
    /*
     * ─────────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [amended C3c.1] THE ORDER IS NO LONGER A DRAFT — **NOT** `ALLOCATION_IN_PROGRESS`.
     *
     *  §9b shipped as `status === 'ALLOCATION_IN_PROGRESS'` and that is NARROWER than the server
     *  refusal it exists to mirror: `updateArrangement` calls
     *  `assertPublishedDutyPointsUnchanged` whenever `doc.status !== 'DRAFT'` — so on a
     *  PUBLISHED, ACTIVE, CLOSED or CANCELLED order the builder went on offering the duty-point
     *  set, its places, its schedule and its policy version, and the API 409'd on save.
     *
     *  ⚠️ AND `PUBLISHED` IS THE ORDINARY CASE, NOT AN EDGE ONE. `markWorkBegun` moves an order
     *  off `PUBLISHED` only when the FIRST officer is named, so every order between publish and
     *  the first name sat in exactly the state the gate missed — which is the defect §9b was
     *  built to close, arriving through the status it forgot.
     *
     *  ⚠️ IT READS THE RECORD RATHER THAN `isDraft`, DELIBERATELY. That value answers `!arrangementId`
     *  on the create route and `false` while `existing` is in flight — the safe direction for a
     *  CONFIRMATION and the wrong one for a lock, which would flash a "duty points are locked"
     *  alert on every draft the edit route opens. Undecided-while-loading is the same choice
     *  `identityLocked` above makes, and the server is the control in both cases.
     * ─────────────────────────────────────────────────────────────────────────────────
     */
    dutyPointsLocked,
    /* ⚠️ FROM THE RECORD, NOT THE DRAFT — see `ArrangementSectionState`. */
    associatedUnits: existing.data?.associatedOrgUnits ?? [],
    /* ⚠️ `isDraft` IS ALREADY THE ONE ANSWER on this page, and it is false until `existing`
       resolves on the edit route — which is the safe direction for a confirmation. */
    isPublished: !isDraft,
    /* [C3a §3] The record's id once it exists, and the files still waiting for one. */
    savedArrangementId: savedId ?? arrangementId,
    pendingAttachments,
    /* ⚠️ [AUTO-SAVE] Only a form with no record queues a file, and with autosave off its save is a press. */
    attachmentsWaitForSave: !autoSave && (savedId ?? arrangementId) === undefined,
  };

  /** ⚠️ THE IMPERATIVE HALF, HANDED TO `IntakeForm` SEPARATELY — see `sections.tsx`. */
  const sectionActions: ArrangementSectionActions = {
    appendPlace,
    appendShift,
    addDutyPoint,
    duplicateDutyPoint,
    removeDutyPoint,
    reorderDutyPoints,
    setRequirementLines,
    addAdHocColumn,
    restrictAdHocColumn,
    removeAdHocColumn,
    setPolicy,
    useCurrentColumns,
    toggleCard,
    toggleShare,
    /* ⚠️ [C2b §8c/§8d] The SAME function `editCards` ends with, so the command path and the
       force path arm autosave identically rather than nearly. */
    commandEntriesChanged: onValuesChange,
    focusLastIn,
    /*
     * ⚠️ [C3a §3] QUEUE AND FLUSH — never a POST of its own. See `pendingAttachments`' header:
     * an independent `POST /arrangements` from here would race the autosave and mint two orders.
     * ⚠️ [AUTO-SAVE] With autosave off the flush writes nothing (`triggerMaySave`), so the file waits
     * for the Save button and the Files section says so (`attachmentsWaitForSave`).
     */
    queueAttachment: (file: File) => {
      /*
       * ⚠️ THE KEY IS MINTED FROM RANDOMNESS, NEVER FROM THE QUEUE'S LENGTH. §7's rule for the
       * fifth time (`slot_key`, `fillRef.util.ts`, `duty_point_id`, the dashboard placement):
       * derive an identity from an identity, never from a POSITION. `${queue.length}:${name}`
       * collides after a removal — drop `0:a` from `[0:a, 1:b]` and the next file is `1:c`
       * against a surviving `1:b` — and the drain's own `filter(item.key !== next.key)` then
       * removes BOTH rows on one success.
       */
      writePending([...pendingRef.current, { key: mintShiftId(), file }]);
      markDirty();
      flush('attach');
    },
    dropQueuedAttachment: (key: string) => {
      writePending(pendingRef.current.filter((item) => item.key !== key));
    },
  };

  if (policies.isLoading || (arrangementId && existing.isLoading)) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }

  return (
    /*
     * ⚠️ THE SHELL IS `components/intake/IntakeForm`, AND THE SECTIONS COME FROM
     * `./sections`. This page owns the DATA — the queries, the form store, the autosave,
     * the publish — and owns none of the LAYOUT. What that buys is in `sections.tsx`'s
     * header: reordering a section, re-stepping it or collapsing the steps is an edit to a
     * literal, and the round-the-clock form is the same shell over a different array.
     */
    <IntakeForm
      form={form}
      rightLabel="Checklist and order preview"
      rightOpen={summaryOpen}
      rightId={SUMMARY_PANEL_ID}
      initialValues={emptyValues()}
      disabled={readOnly}
      onValuesChange={onValuesChange}
      /* ⚠️ [C1f §6] THE FIRST TRIGGER. `focusout` bubbles, so one handler on the form sees
         every field being LEFT — which is the frequency [P3b.3]'s timer never had. */
      onFieldBlur={() => {
        flush('blur');
      }}
      steps={FORM_STEPS}
      step={step}
      /* ⚠️ [C1f §6] THE SECOND TRIGGER. A `Next` refused by `stepBlockers` returns before
         calling this, so a refused step does not save — which is correct. */
      onStepChange={(next) => {
        flush('step');
        setStep(next);
      }}
      labels={{ back: STEP_COPY.back, next: STEP_COPY.next }}
      sections={ARRANGEMENT_SECTIONS}
      state={sectionState}
      actions={sectionActions}
      registerSection={registerSection}
      header={
        <PageHeader
          icon={CalendarClock}
          /*
           * ⚠️ [D4.3] EDITING GOES BACK TO THE RECORD, NOT TO THE LIST. `/arrangements/:id/edit`
           * is reached from the arrangement's own detail page, and skipping over it to the
           * list would lose the operator's place — the one thing a parent link exists to keep.
           * A NEW order has no record to return to, so it goes to the list.
           *
           * ⚠️ [amended TYPED-BACK] …EXCEPT A DRAFT OPENED STRAIGHT FROM A TYPED LIST'S ROW. A typed
           * list's draft row opens THIS page, not the record, so for a draft carrying `?from=` and no
           * `?via=record` there is no page between the row and this form to return to: it goes back to
           * the list — `origin.to`, its page and filters included. A draft opened from its RECORD page
           * (Edit, the blocked Publish, "Take back to draft" — `withRecordOrigin`) keeps [D4.3]'s
           * "Back to the order", and so does an ISSUED order, with the origin carried on so the record
           * then returns to the list. A record that FAILED to load also goes to the list: its record page
           * would fail the same way and offers no back link of its own. `origin` is type-checked above,
           * so an untyped order and a new one never reach the list branch. With no origin, nothing moved.
           *
           * ⚠️ `isDraft`, NEVER `!publishedRecord`. While the record first loads this header is not
           * rendered at all (the spinner above), so no destination is guessed before the record answers;
           * a failed load is its own clause rather than a reading of `isDraft`, whose answer for a record
           * nobody could read ("not a draft") is no evidence of anything.
           */
          parent={
            origin && (existing.isLoadingError || (isDraft && !viaRecord))
              ? { label: origin.label, to: origin.to }
              : arrangementId
                ? { label: 'Back to the order', to: withListOrigin(`/arrangements/${arrangementId}`, origin) }
                : /* ⚠️ [C1f §9] The sidebar's own word — one resolver, three readers. */
                  { label: labelOf(ARRANGEMENTS_NAV, officer?.accessRole), to: ARRANGEMENTS_NAV.to }
          }
          title={displayTitle}
          subtitle={existing.data?.reference}
          actions={
            <>
              {/*
              ⚠️ THE REASONS MOVED OFF THE PAGE AND BEHIND THIS PILL. They used to sit in a
              permanent block above the publish bar, where they took vertical space on every
              render of a screen whose whole point is that the form fits. A pill states the
              FACT ("cannot publish yet") next to the control it is about, and the list —
              which is only read once, when you want to know why — is one click away.
              `type="primary" danger` because it is the only red on the page and it has to
              read as a stop, not as another action.
            */}
              {(blockers.length > 0 || warnings.length > 0) && savedId && (
                <Tooltip title={blockers.length > 0 ? STEP_COPY.warningPillTooltip : STEP_COPY.noticeIntro}>
                  <Button
                    size="small"
                    danger={blockers.length > 0}
                    type={blockers.length > 0 ? 'primary' : 'default'}
                    icon={<TriangleAlert className="size-3.5" />}
                    id="publish-blockers-pill"
                    data-testid="publish-blockers-pill"
                    onClick={() => setBlockersOpen(true)}
                  >
                    {blockers.length > 0 ? STEP_COPY.warningPill : STEP_COPY.noticeTitle}
                  </Button>
                </Tooltip>
              )}
              {/*
                ⚠️ [D4.1] THE ONLY WAY IN, AND IT SAYS WHAT IS BEHIND IT. The checklist and
                the order preview left the page surface so the grid could have the measure;
                a control labelled "Summary" with nothing else to go on would just be a
                mystery button, so `aria-expanded` states the disclosure and the pressed
                styling states it again for anyone who cannot read the attribute.
              */}
              <Button
                size="small"
                type={summaryOpen ? 'primary' : 'default'}
                icon={<PanelRight className="size-3.5" />}
                aria-expanded={summaryOpen}
                aria-controls={SUMMARY_PANEL_ID}
                data-testid="summary-toggle"
                onClick={() => setSummaryOpen((open) => !open)}
              >
                {STEP_COPY.summaryToggle}
              </Button>
              <Button
                type="text"
                icon={<HelpCircle className="size-4" />}
                aria-label={HELP_PANEL.triggerLabel}
                onClick={() => setHelpOpen(true)}
              />
            </>
          }
        />
      }
      right={
        <RightRail
          draft={draft}
          dutyPoints={dutyPoints}
          deploymentCount={deploymentCount}
          slotCount={slotCount}
          divisions={divisions}
          orgUnits={orgUnits}
          displayTitle={displayTitle}
          checklist={checklistOf(ARRANGEMENT_SECTIONS, sectionState)}
          onJump={jump}
        />
      }
      footer={({ stepper }) => (
        <>
          {/*
        ── The publish bar. A seventh SECTION would be a seventh screen. ──────────────
        `-mx`/`-mb` pull it out to <main>'s padding edge so the rule spans the full width
        and nothing is left underneath it. `shrink-0` keeps it at its natural height while
        the form above takes the rest.
      */}
          <div
            className="-mx-4 -mb-6 flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border bg-background px-4 py-3 sm:-mx-6 sm:px-6"
            data-testid="publish-bar"
          >
            {/* Back · Next · the save state — the three things about the FORM, together. */}
            <div className="flex flex-wrap items-center gap-3">
              {stepper}
              {/*
                ⚠️ COLOUR *AND* WORDS. `StatusDot`'s rule — a tone alone says nothing to
                somebody who cannot see it, and the words alone are easy to miss in a bar
                full of buttons. Muted rather than full-strength: unsaved work is a fact to
                notice, not an error to fix.
              */}
              <span
                className={cn(
                  'text-xs',
                  saveState === 'unsaved' && 'text-destructive/80',
                  saveState === 'saved' && 'text-success/90',
                  saveState === 'failed' && 'text-destructive',
                  saveState === 'needs-date' && 'text-destructive/80',
                  (saveState === 'idle' || saveState === 'saving') && 'text-muted-foreground',
                )}
                aria-live="polite"
                data-testid="autosave-indicator"
              >
                {saveState === 'saving' && AUTOSAVE_COPY.saving}
                {saveState === 'saved' && AUTOSAVE_COPY.saved}
                {saveState === 'unsaved' && AUTOSAVE_COPY.unsaved}
                {saveState === 'failed' && AUTOSAVE_COPY.failed}
                {saveState === 'needs-date' && AUTOSAVE_COPY.needsDate}
              </span>
              {/*
                ⚠️ [C5c §8] THE DATE THIS DRAFT WILL BE DELETED, IN ITS LAST WEEK ONLY. Plain text beside
                the save state, which it is about — no breakpoint, no overlay, and nothing at all for an
                issued order, an archived draft or a young one (`draftExpiry.ts`).
              */}
              {expiryNotice !== null && (
                <span className="text-xs text-muted-foreground" data-testid="draft-expiry-notice">
                  {expiryNotice}
                </span>
              )}
              {/*
                ⚠️ [D4.1] HIDING THE PANEL IS FINE; HIDING THE NUMBER IS NOT.
                The checklist and the order preview moved behind a closed-by-default
                sidebar, and this is the one thing in them that must survive that: what an
                operator is about to create.

                ⚠️ IT READS THE LIVE FORM, EXACTLY AS THE PANEL DOES, AND THE FIRST CUT DID
                NOT. That version took four terms from `preview.data` and personnel from the
                form, which is wrong twice over. There is no autosave on this page, and the
                preview only refetches when a save invalidates it — so between saves it holds
                the LAST SAVED answer while the form moves on. The bar read
                `1 duty · 1 duty point · 7 personnel` beside a panel reading
                `2 duties · 2 duty points · … · 7 personnel`, and mixed two snapshots inside
                its own single line. Reproduced in jsdom before it was fixed.

                The server's authoritative count belongs where it already is: the publish
                confirmation, which describes the SAVED record about to be issued. This bar
                describes the form in front of you, which is what "Not saved" beside it is
                also about.
              */}
              <span className="text-xs tabular-nums text-muted-foreground" data-testid="will-create-summary">
                {willCreateSummary({
                  deploymentCount,
                  dutyPointCount: dutyPoints.length,
                  placeCount: dutyPoints.reduce((total, point) => total + point.draft.placeCount, 0),
                  requirementTotals,
                  divisionCount: divisions.length,
                })}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/*
                ⚠️ [D4.5 §2a] FIRST IN THE GROUP, AND DELIBERATELY NOT IN THE LEFT ONE.
                The left group is "the three things about the FORM" — the stepper, the save
                state and what this will create. A destructive act is not one of them. First
                here is the maximum distance from the primary `Publish` at the far end.

                ⚠️ IT IS RENDERED BY STATE AND DISABLED BY CAPABILITY, which are two
                different questions. A published order has no Discard at all — archiving one
                is a CANCELLATION with a different confirmation, and it belongs on the detail
                page that already has it. `!savedId` is the create route, where the status
                has not loaded and there is nothing to publish yet, so the button must not
                flicker in a moment later.

                ⚠️ A NATIVE `title`, NOT AntD's `Tooltip`. Its two neighbours already use
                one, and `e2e/fixtures/overlays.ts` exempts this file at EXACTLY two
                `Tooltip`s — a third would fail the census sweep.
              */}
              {isDraft && (
                <Button
                  danger
                  disabled={readOnly || !canDelete}
                  loading={discard.isPending}
                  data-testid="discard"
                  title={readOnly || !canDelete ? READ_ONLY_TOOLTIP : DISCARD_COPY.tooltip}
                  onClick={() => setDiscardOpen(true)}
                >
                  {DISCARD_COPY.action}
                </Button>
              )}
              {/*
                ─────────────────────────────────────────────────────────────────────────
                 ⚠️ [C1f §7] ONE CONTROL, FOUR MEANINGS — AND THREE BUTTONS ARE GONE.

                 `Save draft` went because §6 restored autosave; `Assign names myself` went as
                 a BUTTON while its publish intent stays on the wire untouched; and the
                 qualifier on `Publish and request divisions` went because the button it
                 distinguished this one from is no longer there. `publishControl.ts` states
                 all three as the reversal of [P3a]'s toolbar that they are.

                 ⚠️ §7b: `blocked` AND `unsaved` LOOK UNAVAILABLE AND ARE GENUINELY CLICKABLE.
                 [P4-UX]'s click-through `<span>` is DELETED — it existed only because a
                 `disabled` button has `pointer-events: none` — so the keyboard path is the
                 button itself for the first time. `aria-disabled` was refused for a second,
                 measured reason: playwright 1.62's `click` waits on `enabled` and honours it,
                 so the browser assertion would be testing a gesture no operator makes.

                 ⚠️ `refused` ALONE STAYS `disabled`, with the app-wide read-only tooltip. A
                 blocker is a to-do list the operator can act on; a capability refusal is not.
                ─────────────────────────────────────────────────────────────────────────
              */}
              {/*
                ─────────────────────────────────────────────────────────────────────────
                 ⚠️ [C3a §7a] WHERE A SAVE IS OWED, THIS IS A `Save` AND PUBLISH IS NOT RENDERED.

                 Two controls in one slot, never both — §7a literally. `publishControlState` is
                 the ONE derivation of which; the two branches below just render its answer.

                 ⚠️ IT IS NOT [C1f §7]'s `Save draft` RESTORED. That was a permanent control that
                 autosave made redundant; this appears only while `saveState` says a write is
                 owed, and §7b's own test is that seeing it in the ORDINARY case means autosave
                 is broken. `SAVE_COPY.action` is `Save` for that reason — the old name would
                 read as the old button.

                 ⚠️ A NATIVE `title`, NOT AntD's `Tooltip`. `e2e/fixtures/overlays.ts` exempts
                 this file at EXACTLY two `Tooltip`s — the blockers pill and the meaning-changing
                 control — and `census.spec.ts`'s per-file arithmetic is exact, so a third fails
                 the sweep. `discard` a few lines above solved it the same way and says so.

                 ⚠️ AND IT IS `disabled` ON `idle`, WHICH CLOSES A LIVE HOLE. Today an untouched
                 `/arrangements/new` shows an unavailable-LOOKING Publish that is genuinely
                 clickable and, pressed, calls `flush` → `armingOf` → `'no-content'` → returns
                 SILENTLY. A control that does nothing is what [C1f §7b] forbids, and it has
                 shipped since that pass.
                ─────────────────────────────────────────────────────────────────────────
              */}
              {control === 'save' ? (
                <Button
                  type="primary"
                  loading={save.isPending}
                  /*
                    ⚠️ [C3c §10] `|| save.isPending` — THE SECOND HALF OF THE TWO-CLICK PUBLISH.

                    MEASURED in the installed antd 5.29 (`es/button/button.js`): `handleClick`
                    opens `if (innerLoading || mergedDisabled) { e.preventDefault(); return; }`,
                    so a `loading` Button DISCARDS the click before `onClick` runs — while still
                    LOOKING pressable, because `loading` alone sets no `disabled` attribute.

                    That is what an operator met. Pressing this control blurs the field they were
                    typing in, `IntakeForm`'s `onBlur` fires `flush('blur')`, the PATCH starts, and
                    by mouseup the button is `loading` and eats the gesture. Their first click did
                    nothing; the save landed, `markClean('saved')` moved `publishControlState` off
                    `'save'`, Publish rendered in this same slot, and the second click published.

                    `loading` STAYS for the spinner. `disabled` is what makes the refusal honest —
                    announced to a screen reader and drawn as unavailable — which is [C1f §7b]'s
                    "a control that does nothing" rule, met on this branch for the first time.
                  */
                  disabled={saveState === 'idle' || save.isPending}
                  data-testid="save"
                  data-state={saveState}
                  title={SAVE_COPY.tooltip(saveState, publishedRecord)}
                  onClick={() => {
                    flush('save-button');
                  }}
                >
                  {SAVE_COPY.action}
                </Button>
              ) : (
                <Tooltip title={controlTitle}>
                  <Button
                    type={control === 'published' || control === 'ready' ? 'primary' : 'default'}
                    loading={publish.isPending || save.isPending}
                    /*
                      ⚠️ [amended C3c.1] `|| save.isPending` — THE SIBLING BRANCH KEPT THE EXACT
                      CLICK-DISCARD §10 FIXED ON `save`.

                      antd 5.29's `handleClick` early-returns on `innerLoading` while rendering
                      `disabled: mergedDisabled`, so `loading` alone eats the gesture and sets no
                      attribute. `publishControlState` tests `publishedRecord` BEFORE
                      `saveIsPending`, so the `published` arm renders here — not on the `save`
                      branch — while a save is in flight: a nodal editing a force count on a
                      PUBLISHED order (still permitted; §9b freezes only the structure) blurs the
                      field, the PATCH starts, and their click on "Assign names" does nothing.

                      ⚠️ `refused` STAYS SEPARATELY DISABLED, and `blocked` stays CLICKABLE — that
                      is [C1f §7b]'s split and it is untouched: a blocker is a to-do list the
                      operator can open, a capability refusal is not.
                    */
                    disabled={control === 'refused' || save.isPending}
                    className={UNAVAILABLE_LOOK[control]}
                    data-testid="publish"
                    data-state={control}
                    aria-describedby={control === 'blocked' ? 'publish-blockers-pill' : undefined}
                    onClick={() => {
                      if (control === 'refused') return;
                      if (control === 'published') {
                        /* ⚠️ [TYPED-BACK] The origin rides on, so the record's own back link returns to the list. */
                        void navigate(withListOrigin(`/arrangements/${savedId ?? arrangementId ?? ''}`, origin));
                        return;
                      }
                      if (control === 'blocked') {
                        setBlockersOpen(true);
                        return;
                      }
                      modal.confirm({
                        title: PUBLISH_COPY.confirmTitle,
                        /*
                         * ─────────────────────────────────────────────────────────────────────
                         *  ⚠️ [amended C2a §4a] A PLAIN CONFIRMATION — A CLIENT DECISION THAT
                         *  REVERSES [C1f §7c], AND THE REVERSAL IS WRITTEN DOWN RATHER THAN LEFT
                         *  AS COMMENTED-OUT JSX.
                         *
                         *  This block named what would be created, listed the divisions about to
                         *  be asked, and repeated the publish warnings. `feat/policy` "simplified"
                         *  it by commenting all three out — which left the dead JSX in the file,
                         *  `willCreate` imported and unused (a typecheck error), and the comment
                         *  above still promising content that no longer rendered. Deleted properly
                         *  here.
                         *
                         *  ⚠️ [D4.1] §68's INVARIANT IS INTACT, AND THAT IS WHAT MAKES THIS SAFE.
                         *  "Whatever the panel hides, the NUMBER stays out" — the count did not
                         *  live only here. `willCreateSummary` still renders in the publish bar a
                         *  few lines above, beside the button being pressed, so the operator is
                         *  still told what they are about to create before they press it.
                         *
                         *  ⚠️ AND THE WARNINGS ARE STILL REACHABLE. `publish-blockers-pill` opens
                         *  the same dialog on its warnings title whenever nothing is blocking,
                         *  which is [C1f §7c]'s own stated fallback. What is given up is that
                         *  they are no longer repeated at the moment of confirmation — stated as
                         *  a cost, not claimed away.
                         * ─────────────────────────────────────────────────────────────────────
                         */
                        content: (
                          <div className="flex flex-col gap-2 text-sm">
                            <span className="text-muted-foreground">{PUBLISH_COPY.confirmIrreversible}</span>
                          </div>
                        ),
                        okText: 'Publish',
                        onOk: () => {
                          publish.mutate('REQUEST_DIVISIONS');
                        },
                      });
                    }}
                  >
                    {control === 'published' ? PUBLISH_COPY.assignNames : 'Publish'}
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        </>
      )}
    >
      {/*
        ⚠️ [D4.5 §2a] `ConfirmDestructive`, NOT `modal.confirm`, AND IT HAD NO CONSUMER.
        [LIFECYCLE] built it as "one confirmation for every destructive act" and nothing ever
        called it — `e2e/fixtures/overlays.ts` recorded the outstanding choice as "either wire
        it to the archive confirmations it was written for or delete it". This is the wiring.
        It also owns the `<Modal>`, so this file's census count is unchanged.

        ⚠️ NO `requireTyped`. Its header reserves typing for archiving a PUBLISHED order and
        for permanent deletes; a draft is neither, and a confirmation that always demands
        typing is one an operator learns to defeat rather than to read.
      */}
      <ConfirmDestructive
        open={discardOpen}
        title={DISCARD_COPY.title}
        okText={DISCARD_COPY.okText}
        confirmLoading={discard.isPending}
        body={<ArchiveNote>{savedId ? DISCARD_COPY.saved : DISCARD_COPY.unsaved}</ArchiveNote>}
        onCancel={() => setDiscardOpen(false)}
        onConfirm={() => {
          /* Nothing was ever created, so there is nothing to archive — just close the form. */
          if (!savedId) {
            setDiscardOpen(false);
            leaveAfterDiscard();
            return;
          }
          discard.mutate();
        }}
      />

      <Modal
        open={helpOpen}
        onCancel={() => setHelpOpen(false)}
        footer={null}
        title={HELP_PANEL.title}
        data-testid="help-panel"
      >
        {(autoSave ? HELP_PANEL.paragraphs : HELP_PANEL.manualSaveParagraphs).map((paragraph) => (
          <p key={paragraph.slice(0, 24)} className="mb-3 text-sm">
            {paragraph}
          </p>
        ))}
      </Modal>

      {/*
        ⚠️ TWO LISTS, ONE DIALOG, AND THE DIFFERENCE BETWEEN THEM IS STATED RATHER THAN
        IMPLIED. Publishing is still REFUSED with reasons rather than allowed and then
        failed — but only for the blockers. Mixing an unstaffed command role in with "no
        dates set" taught the operator that everything in this box stops them, which is how
        a warning gets ignored and a blocker gets argued with.
      */}
      <Modal
        open={blockersOpen}
        onCancel={() => setBlockersOpen(false)}
        footer={null}
        title={blockers.length > 0 ? STEP_COPY.blockedTitle : STEP_COPY.noticeTitle}
        data-testid="publish-blockers"
      >
        {blockers.length > 0 && (
          <>
            <p className="mb-2 text-sm">{PUBLISH_COPY.blockedIntro}</p>
            <ul className="mb-4 list-inside list-disc text-sm">
              {blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          </>
        )}
        {/*
          ⚠️ [amended C1f §7c] THE WARNINGS ARE SHOWN ONLY WHEN NOTHING IS BLOCKING.
          [P3a.2] drew the blocker/warning line and it holds: a box that mixes "no dates set"
          with an unstaffed command role teaches the operator that everything in it stops
          them, which is how a warning gets ignored and a blocker gets argued with. §7c makes
          the box that REFUSES carry refusals alone.
          ⚠️ NOTHING VANISHES. The pill opens this dialog on its warnings title whenever there
          is nothing blocking, and the publish confirmation now names them at the moment
          publishing anyway is actually decided.
        */}
        {blockers.length === 0 && warnings.length > 0 && (
          <>
            <p className="mb-2 text-sm">{STEP_COPY.noticeIntro}</p>
            <ul className="list-inside list-disc text-sm" data-testid="publish-warnings">
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </>
        )}
      </Modal>

      {readOnly && <Alert type="info" showIcon message="Your access is view-only." />}
    </IntakeForm>
  );
};

export default ArrangementBuilderPage;
